import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AppShell from "../components/classic/AppShell";
import ClassicWindow from "../components/classic/ClassicWindow";
import Toolbar, { ToolbarButton, ToolbarSeparator } from "../components/classic/Toolbar";
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Checkbox,
  Drawer,
  Empty,
  Input,
  Modal as GoldModal,
  Progress,
  Select,
  Spinner,
  Tab,
  Table,
  goldAlert,
  goldToast,
} from "gold-ui-kit";
import { Minus, Plus, Search, Trash2 } from "lucide-react";
import { toLocalDateKey } from "../utils/date";
import { useAuth } from "../context/AuthContext";
import zreportapi from "../services/zreport";
import {
  addReport,
  updateReport,
  deleteReport,
  addReportRow,
  updateReportRow,
  deleteReportRow,
  setDailyExtra,
  addDailyPayment,
  updateDailyPayment,
  deleteDailyPayment,
  closeDay,
  reopenReports,
  setReportsForDate,
} from "../store/slices/zRaporSlice";
import {
  fetchCashierPayments,
  addCashierPayment,
  updateCashierPayment,
  deleteCashierPayment,
} from "../store/slices/zreports/cashier-payments";
import {
  fetchCoinSettlements,
  addCoinSettlement,
  updateCoinSettlement,
  deleteCoinSettlement,
} from "../store/slices/zreports/coin-settlements";
import {
  fetchDebtInformations,
  addDebtInformation,
  updateDebtInformation,
  deleteDebtInformation,
} from "../store/slices/zreports/debt-informations";
import {
  fetchAllZReportDefs,
  fetchBanks,
  addBank,
  updateBank,
  deleteBank,
  fetchDebts,
  addDebt,
  updateDebt,
  deleteDebt,
  fetchExpenses,
  addExpense,
  updateExpense,
  deleteExpense,
  fetchCases,
  addCase,
  updateCase,
  deleteCase,
  fetchCashiers,
  addCashier,
  updateCashier,
  deleteCashier,
} from "../store/slices/zreports";

/* ============================================================ */
const today = () => toLocalDateKey();
const fmt = (n) =>
  Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 });
const sumBy = (arr, fn) => arr.reduce((s, x) => s + Number(fn(x) || 0), 0);

const tahsilatToplam = (r) =>
  Number(r.nakit || 0) +
  Number(r.hediyeKart || 0) +
  sumBy(r.visa || [], (v) => v.tutar) +
  sumBy(r.borclar || [], (b) => b.tutar) +
  sumBy(r.giderler || [], (g) => g.tutar);

const farkOf = (r) => tahsilatToplam(r) - Number(r.toplamTutar || 0);

// Builds GoldUI Select options with a leading empty choice.
const toOptions = (list, getLabel, placeholder) => [
  ...(placeholder !== undefined ? [{ value: "", label: placeholder }] : []),
  ...(list || []).map((x) => ({ value: x.id, label: getLabel(x) })),
];

const FieldLabel = ({ children }) => <span className="text-[11px] font-semibold text-slate-700">{children}</span>;

const confirmDelete = async (title, text) => {
  const result = await goldAlert.fire({
    title,
    text,
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Evet, Sil",
    cancelButtonText: "Vazgeç",
    confirmButtonColor: "danger",
    cancelButtonColor: "default",
  });
  return result.isConfirmed;
};

/* ============================================================
   Section sub-component (Visa / Gider / Borç)
============================================================ */
const Section = ({ title, rows, onAdd, onDelete, renderRow, emptyText, disabled }) => (
  <div className="mt-2 border-t border-slate-100 pt-2">
    <div className="mb-1.5 flex items-center justify-between">
      <div className="text-[11px] font-bold tracking-wide text-slate-500">{title}</div>
      {!disabled && (
        <Button type="button" size="xs" variant="filled" color="primary" iconOnly onClick={onAdd} title="Yeni satır">
          <Plus size={12} />
        </Button>
      )}
    </div>
    {rows.length === 0 ? (
      <div className="py-1 text-[11px] italic text-slate-400">{emptyText}</div>
    ) : (
      <div className="flex flex-col gap-1.5">
        {rows.map((r) => (
          <div key={r.id} className="flex items-center gap-1">
            {renderRow(r)}
            {!disabled && (
              <Button type="button" size="xs" variant="filled" color="danger" iconOnly onClick={() => onDelete(r.id)} title="Sil">
                <Minus size={12} />
              </Button>
            )}
          </div>
        ))}
      </div>
    )}
  </div>
);

const AmountInput = ({ value, onChange, disabled, width }) => (
  <div style={width ? { width } : undefined} className={width ? "shrink-0" : "w-full"}>
    <Input
      size="sm"
      type="number"
      step="0.01"
      className="!text-right"
      value={value || ""}
      placeholder="0"
      onChange={(e) => onChange(Number(e.target.value))}
      disabled={disabled}
    />
  </div>
);

/* ============================================================
   Z-Rapor Card
============================================================ */
const ZRaporCard = ({ report }) => {
  const dispatch = useDispatch();
  const defs = useSelector((s) => s.zRaporDefs);

  const upd = (patch) => dispatch(updateReport({ id: report.id, patch }));
  const updRow = (section, rowId, patch) => dispatch(updateReportRow({ id: report.id, section, rowId, patch }));
  const addRow = (section, row) => dispatch(addReportRow({ id: report.id, section, row }));
  const delRow = (section, rowId) => dispatch(deleteReportRow({ id: report.id, section, rowId }));

  const fark = farkOf(report);
  const farkAbs = Math.abs(fark).toFixed(2);
  let badge = null;
  if (Number(report.toplamTutar) > 0 || tahsilatToplam(report) > 0) {
    if (Math.abs(fark) < 0.01) badge = { text: "TAM", color: "default" };
    else if (fark > 0) badge = { text: `FAZLA +${farkAbs}`, color: "success" };
    else badge = { text: `EKSİK −${farkAbs}`, color: "danger" };
  }

  const isClosed = report.status === "closed";
  const balanced = isClosed || (Math.abs(fark) < 0.01 && Number(report.toplamTutar) > 0);

  const handleDelete = async () => {
    if (isClosed) return;
    if (await confirmDelete(`${report.raporNo || "Rapor"} silinsin mi?`, "Bu rapor kartı silinecek.")) {
      dispatch(deleteReport(report.id));
    }
  };

  return (
    <Box
      className="flex flex-col !overflow-visible !rounded-2xl !border-slate-200 !p-0 !shadow-sm"
      style={{ width: 320 }}
      data-testid={`zrapor-card-${report.id}`}
    >
      <div className="flex items-center justify-between rounded-t-2xl border-b border-slate-100 bg-slate-50 px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="text-[14px] font-bold">{report.raporNo || "—"}</span>
          {badge && (
            <Badge size="xs" variant="soft" color={badge.color} data-testid={`zrapor-badge-${report.id}`}>
              {badge.text}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-block h-2.5 w-2.5 rounded-full ${balanced ? "bg-emerald-500" : "bg-red-500"}`} title={isClosed ? "Kapatıldı" : balanced ? "Dengede" : "Fark var"} />
          {!isClosed && (
            <Button size="xs" variant="text" color="danger" iconOnly onClick={handleDelete} title="Sil" data-testid={`zrapor-delete-${report.id}`}>
              <Trash2 size={13} />
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-3 p-3">
        <div className="flex flex-col gap-1">
          <FieldLabel>Rapor Tarihi</FieldLabel>
          <Input
            size="sm"
            type="date"
            value={report.date}
            onChange={(e) => upd({ date: e.target.value })}
            disabled={isClosed}
            data-testid={`zrapor-${report.id}-date`}
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col gap-1">
            <FieldLabel>Kasiyer Adı</FieldLabel>
            <Select
              size="sm"
              options={toOptions(defs.kasiyerler, (k) => `${k.name} ${k.surname || ""}`.trim(), "— Seç —")}
              value={report.kasiyerId}
              onChange={(e) => upd({ kasiyerId: e.target.value })}
              disabled={isClosed}
            />
          </div>
          <div className="flex flex-col gap-1">
            <FieldLabel>Kasa</FieldLabel>
            <Select
              size="sm"
              options={toOptions(defs.kasalar, (k) => `${k.no} - ${k.name}`, "— Seç —")}
              value={report.kasaId}
              onChange={(e) => upd({ kasaId: e.target.value })}
              disabled={isClosed}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <FieldLabel>Rapor No</FieldLabel>
          <Input
            size="sm"
            value={report.raporNo}
            onChange={(e) => upd({ raporNo: e.target.value })}
            disabled={isClosed}
            data-testid={`zrapor-${report.id}-rapor-no`}
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col gap-1">
            <FieldLabel>Z-Rapor Tutarı</FieldLabel>
            <AmountInput value={report.toplamTutar} onChange={(v) => upd({ toplamTutar: v })} disabled={isClosed} />
          </div>
          <div className="flex flex-col gap-1">
            <FieldLabel>Müşteri Sayısı</FieldLabel>
            <Input
              size="sm"
              type="number"
              className="!text-right"
              value={report.musteriSayisi || ""}
              placeholder="0"
              onChange={(e) => upd({ musteriSayisi: Number(e.target.value) })}
              disabled={isClosed}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col gap-1">
            <FieldLabel>Nakit Tutar</FieldLabel>
            <AmountInput value={report.nakit} onChange={(v) => upd({ nakit: v })} disabled={isClosed} />
          </div>
          <div className="flex flex-col gap-1">
            <FieldLabel>Hediye Kart</FieldLabel>
            <AmountInput value={report.hediyeKart} onChange={(v) => upd({ hediyeKart: v })} disabled={isClosed} />
          </div>
        </div>

        <Section
          title="VİSA BİLGİLERİ"
          rows={report.visa || []}
          onAdd={() => addRow("visa", { bankaId: "", tutar: 0 })}
          onDelete={(id) => delRow("visa", id)}
          emptyText="Kayıt yok"
          disabled={isClosed}
          renderRow={(r) => (
            <>
              <div className="min-w-0 flex-1">
                <Select
                  size="sm"
                  options={toOptions(defs.bankalar, (b) => b.name, "— Banka —")}
                  value={r.bankaId}
                  onChange={(e) => updRow("visa", r.id, { bankaId: e.target.value })}
                  disabled={isClosed}
                />
              </div>
              <AmountInput width={96} value={r.tutar} onChange={(v) => updRow("visa", r.id, { tutar: v })} disabled={isClosed} />
            </>
          )}
        />

        <Section
          title="GİDER KATEGORİSİ"
          rows={report.giderler || []}
          onAdd={() => addRow("giderler", { kategoriId: "", tutar: 0, aciklama: "" })}
          onDelete={(id) => delRow("giderler", id)}
          emptyText="Kayıt yok"
          disabled={isClosed}
          renderRow={(r) => (
            <>
              <div className="min-w-0 flex-1">
                <Select
                  size="sm"
                  options={toOptions(defs.giderKategoriler, (g) => g.name, "— Kategori —")}
                  value={r.kategoriId}
                  onChange={(e) => updRow("giderler", r.id, { kategoriId: e.target.value })}
                  disabled={isClosed}
                />
              </div>
              <AmountInput width={96} value={r.tutar} onChange={(v) => updRow("giderler", r.id, { tutar: v })} disabled={isClosed} />
            </>
          )}
        />

        <Section
          title="BORÇ"
          rows={report.borclar || []}
          onAdd={() => addRow("borclar", { musteriId: "", tutar: 0, aciklama: "" })}
          onDelete={(id) => delRow("borclar", id)}
          emptyText="Kayıt yok"
          disabled={isClosed}
          renderRow={(r) => (
            <>
              <div className="min-w-0 flex-1">
                <Select
                  size="sm"
                  options={toOptions(defs.borcMusteriler, (m) => m.name, "— Müşteri —")}
                  value={r.musteriId}
                  onChange={(e) => updRow("borclar", r.id, { musteriId: e.target.value })}
                  disabled={isClosed}
                />
              </div>
              <AmountInput width={96} value={r.tutar} onChange={(v) => updRow("borclar", r.id, { tutar: v })} disabled={isClosed} />
            </>
          )}
        />
      </div>
    </Box>
  );
};

/* ============================================================
   Modals
============================================================ */
const Modal = ({ title, onClose, children, width = 720 }) => (
  <GoldModal isOpen onClose={onClose} title={title} width={width} className="max-w-[95vw]">
    <div className="max-h-[75vh] overflow-auto">{children}</div>
  </GoldModal>
);

/* ============================================================
   Tanımlar modal
============================================================ */
const SECTIONS = [
  { key: "kasiyerler", title: "Kasiyerler", fields: [{ key: "name", label: "Ad" }, { key: "surname", label: "Soyad" }] },
  { key: "kasalar", title: "Kasalar", fields: [{ key: "no", label: "No" }, { key: "name", label: "Ad" }] },
  { key: "bankalar", title: "Bankalar", fields: [{ key: "name", label: "Ad" }] },
  { key: "giderKategoriler", title: "Gider Kategorileri", fields: [{ key: "name", label: "Ad" }] },
  { key: "borcMusteriler", title: "Borç Müşterileri", fields: [{ key: "name", label: "Ad" }] },
];

const TanimlarModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const defs = useSelector((s) => s.zRaporDefs);
  const [active, setActive] = useState("kasiyerler");
  const sec = SECTIONS.find((s) => s.key === active);
  const [form, setForm] = useState({});

  const items = defs[active] || [];

  const handleAdd = () => {
    if (!sec.fields[0] || !form[sec.fields[0].key]) {
      goldToast.warning("Lütfen alanları doldurun.");
      return;
    }
    if (active === "kasiyerler") {
      dispatch(addCashier({ name: form.name, surname: form.surname || "" })).then(() => {
        if (user?.branchId) dispatch(fetchCashiers(user.branchId));
      });
    } else if (active === "kasalar") {
      const title = `${form.no || ""} - ${form.name || ""}`;
      dispatch(addCase({ title })).then(() => {
        if (user?.branchId) dispatch(fetchCases(user.branchId));
      });
    } else if (active === "bankalar") {
      dispatch(addBank({ title: form.name })).then(() => dispatch(fetchBanks()));
    } else if (active === "giderKategoriler") {
      dispatch(addExpense({ title: form.name })).then(() => dispatch(fetchExpenses()));
    } else if (active === "borcMusteriler") {
      dispatch(addDebt({ title: form.name })).then(() => dispatch(fetchDebts()));
    }
    setForm({});
  };

  const handleUpdate = (it, fieldKey, newValue) => {
    const updatedForm = { ...it, [fieldKey]: newValue };
    if (active === "kasiyerler") {
      dispatch(updateCashier({ id: it.id, name: updatedForm.name, surname: updatedForm.surname || "" }));
    } else if (active === "kasalar") {
      const title = `${updatedForm.no || ""} - ${updatedForm.name || ""}`;
      dispatch(updateCase({ id: it.id, title }));
    } else if (active === "bankalar") {
      dispatch(updateBank({ id: it.id, title: updatedForm.name }));
    } else if (active === "giderKategoriler") {
      dispatch(updateExpense({ id: it.id, title: updatedForm.name }));
    } else if (active === "borcMusteriler") {
      dispatch(updateDebt({ id: it.id, title: updatedForm.name }));
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirmDelete("Kayıt silinsin mi?", "Bu tanım kalıcı olarak silinecek."))) return;
    if (active === "kasiyerler") dispatch(deleteCashier({ id }));
    else if (active === "kasalar") dispatch(deleteCase({ id }));
    else if (active === "bankalar") dispatch(deleteBank({ id }));
    else if (active === "giderKategoriler") dispatch(deleteExpense({ id }));
    else if (active === "borcMusteriler") dispatch(deleteDebt({ id }));
  };

  const columns = [
    { key: "no", dataIndex: "id", title: "#", width: 48, align: "center", render: (it) => items.indexOf(it) + 1 },
    ...sec.fields.map((f) => ({
      key: f.key,
      dataIndex: f.key,
      title: f.label,
      render: (it) => <Input size="sm" value={it[f.key] || ""} onChange={(e) => handleUpdate(it, f.key, e.target.value)} />,
    })),
    {
      key: "actions",
      dataIndex: "id",
      title: "",
      width: 56,
      align: "center",
      render: (it) => (
        <Button size="xs" variant="text" color="danger" iconOnly onClick={() => handleDelete(it.id)} title="Sil">
          <Trash2 size={14} />
        </Button>
      ),
    },
  ];

  return (
    <Modal title="Tanımlar" onClose={onClose}>
      <div className="grid h-[60vh] grid-cols-1 gap-4 sm:grid-cols-[190px_1fr]">
        <div className="flex flex-col gap-1 border-slate-100 sm:border-r sm:pr-3">
          {SECTIONS.map((s) => (
            <Button
              key={s.key}
              size="sm"
              variant={active === s.key ? "solid" : "text"}
              color={active === s.key ? "primary" : "dark"}
              className="!w-full !justify-between"
              onClick={() => {
                setActive(s.key);
                setForm({});
              }}
            >
              <span>{s.title}</span>
              <Badge size="xs" radius="full" variant="soft" color={active === s.key ? "default" : "primary"}>
                {defs[s.key]?.length || 0}
              </Badge>
            </Button>
          ))}
        </div>
        <div className="flex min-w-0 flex-col gap-3 overflow-auto">
          <div className="flex flex-wrap items-end gap-2 border-b border-slate-100 pb-3">
            {sec.fields.map((f) => (
              <div key={f.key} className="flex min-w-[120px] flex-1 flex-col gap-1">
                <FieldLabel>{f.label}</FieldLabel>
                <Input
                  size="sm"
                  value={form[f.key] || ""}
                  onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                />
              </div>
            ))}
            <Button size="sm" onClick={handleAdd}>
              <span className="flex items-center gap-1">
                <Plus size={14} /> Ekle
              </span>
            </Button>
          </div>
          <Table size="sm" uniqueKey="id" columns={columns} dataSource={items} locale={{ emptyText: "Kayıt yok", emptyHint: "Yukarıdan yeni kayıt ekleyin." }} />
        </div>
      </div>
    </Modal>
  );
};

/* ============================================================
   Ödemeler modal (Bozuk para + Geçiş borç + Kasa şef ödemeleri)
   Daily/global — not per report
============================================================ */
const NEW_ROW_KEY = "__new__";

const OdemelerModal = ({ date, onClose }) => {
  const dispatch = useDispatch();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState("sef");

  const cashierPayments = useSelector((s) => s.cashierPayments.items || []);
  const coinSettlements = useSelector((s) => s.coinSettlements.items || []);
  const debtInformations = useSelector((s) => s.debtInformations.items || []);

  useEffect(() => {
    if (user?.branchId && date) {
      dispatch(fetchCashierPayments({ branchId: user.branchId, transactionDate: date }));
      dispatch(fetchCoinSettlements({ branchId: user.branchId, transactionDate: date }));
      dispatch(fetchDebtInformations({ branchId: user.branchId, transactionDate: date }));
    }
  }, [dispatch, user?.branchId, date]);

  const reloadCashierPayments = () => {
    if (user?.branchId && date) dispatch(fetchCashierPayments({ branchId: user.branchId, transactionDate: date }));
  };
  const reloadCoinSettlements = () => {
    if (user?.branchId && date) dispatch(fetchCoinSettlements({ branchId: user.branchId, transactionDate: date }));
  };
  const reloadDebtInformations = () => {
    if (user?.branchId && date) dispatch(fetchDebtInformations({ branchId: user.branchId, transactionDate: date }));
  };

  const [newDesc, setNewDesc] = useState("");
  const [newAmount, setNewAmount] = useState("");
  const [newCoinAmount, setNewCoinAmount] = useState("");
  const [newDebtAmount, setNewDebtAmount] = useState("");
  const [newDebtDesc, setNewDebtDesc] = useState("");

  const handleKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const descVal = newDesc.trim();
    const amountVal = Number(newAmount || 0);
    if (!descVal) {
      goldToast.warning("Lütfen açıklama girin.");
      return;
    }
    if (amountVal <= 0) {
      goldToast.warning("Lütfen geçerli bir tutar girin.");
      return;
    }
    dispatch(addCashierPayment({ description: descVal, totalAmount: amountVal, transactionDate: date, branchId: user?.branchId }))
      .unwrap()
      .then(() => {
        setNewDesc("");
        setNewAmount("");
        reloadCashierPayments();
      })
      .catch((err) => goldToast.error(err || "Ödeme eklenemedi."));
  };

  const handleUpdatePaymentDescription = (p, value) => {
    if (value === p.description) return;
    dispatch(updateCashierPayment({ id: p.id, payment: { description: value, totalAmount: p.totalAmount, transactionDate: date, branchId: user?.branchId } }))
      .unwrap()
      .then(reloadCashierPayments);
  };

  const handleUpdatePaymentAmount = (p, value) => {
    const val = Number(value || 0);
    if (val === p.totalAmount) return;
    dispatch(updateCashierPayment({ id: p.id, payment: { description: p.description, totalAmount: val, transactionDate: date, branchId: user?.branchId } }))
      .unwrap()
      .then(reloadCashierPayments);
  };

  const handleDeletePayment = async (id) => {
    if (await confirmDelete("Ödeme silinsin mi?", "Bu kasa şefi ödemesi silinecek.")) {
      dispatch(deleteCashierPayment(id)).unwrap().then(reloadCashierPayments);
    }
  };

  const handleCoinKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const amountVal = Number(newCoinAmount || 0);
    if (amountVal <= 0) {
      goldToast.warning("Lütfen geçerli bir tutar girin.");
      return;
    }
    dispatch(addCoinSettlement({ totalAmount: amountVal, transactionDate: date, branchId: user?.branchId }))
      .unwrap()
      .then(() => {
        setNewCoinAmount("");
        reloadCoinSettlements();
      })
      .catch((err) => goldToast.error(err || "Bozuk para eklenemedi."));
  };

  const handleUpdateCoinAmount = (p, value) => {
    const val = Number(value || 0);
    if (val === p.totalAmount) return;
    dispatch(updateCoinSettlement({ id: p.id, settlement: { totalAmount: val, transactionDate: date, branchId: user?.branchId } }))
      .unwrap()
      .then(reloadCoinSettlements);
  };

  const handleDeleteCoin = async (id) => {
    if (await confirmDelete("Kayıt silinsin mi?", "Bu bozuk para kaydı silinecek.")) {
      dispatch(deleteCoinSettlement(id)).unwrap().then(reloadCoinSettlements);
    }
  };

  const handleDebtKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const descVal = newDebtDesc.trim() || "Geçiş Borç";
    const amountVal = Number(newDebtAmount || 0);
    if (amountVal <= 0) {
      goldToast.warning("Lütfen geçerli bir tutar girin.");
      return;
    }
    dispatch(addDebtInformation({ totalAmount: amountVal, description: descVal, transactionDate: date, branchId: user?.branchId }))
      .unwrap()
      .then(() => {
        setNewDebtDesc("");
        setNewDebtAmount("");
        reloadDebtInformations();
      })
      .catch((err) => goldToast.error(err || "Geçiş borç eklenemedi."));
  };

  const handleUpdateDebtDescription = (p, value) => {
    const val = value.trim() || "Geçiş Borç";
    if (val === p.description) return;
    dispatch(updateDebtInformation({ id: p.id, debtInfo: { totalAmount: p.totalAmount, description: val, transactionDate: date, branchId: user?.branchId } }))
      .unwrap()
      .then(reloadDebtInformations);
  };

  const handleUpdateDebtAmount = (p, value) => {
    const val = Number(value || 0);
    if (val === p.totalAmount) return;
    dispatch(updateDebtInformation({ id: p.id, debtInfo: { totalAmount: val, description: p.description || "Geçiş Borç", transactionDate: date, branchId: user?.branchId } }))
      .unwrap()
      .then(reloadDebtInformations);
  };

  const handleDeleteDebt = async (id) => {
    if (await confirmDelete("Kayıt silinsin mi?", "Bu geçiş borç kaydı silinecek.")) {
      dispatch(deleteDebtInformation(id)).unwrap().then(reloadDebtInformations);
    }
  };

  const sefTotal = sumBy(cashierPayments, (k) => k.totalAmount);
  const coinTotal = sumBy(coinSettlements, (k) => k.totalAmount);
  const debtTotal = sumBy(debtInformations, (k) => k.totalAmount);

  const isNew = (r) => r.id === NEW_ROW_KEY;
  const withNewRow = (rows) => [...rows, { id: NEW_ROW_KEY }];

  const noColumn = (rows) => ({
    key: "no",
    dataIndex: "id",
    title: "#",
    width: 48,
    align: "center",
    render: (r) => (isNew(r) ? <Plus size={14} className="mx-auto text-blue-600" /> : rows.indexOf(r) + 1),
  });

  const deleteColumn = (onDelete) => ({
    key: "actions",
    dataIndex: "id",
    title: "",
    width: 64,
    align: "center",
    render: (r) =>
      isNew(r) ? (
        <Badge size="xs" variant="soft" color="default">
          Enter
        </Badge>
      ) : (
        <Button size="xs" variant="text" color="danger" iconOnly onClick={() => onDelete(r.id)} title="Sil">
          <Trash2 size={14} />
        </Button>
      ),
  });

  const amountCell = (r, { newValue, setNewValue, onKeyDown, onBlur, placeholder = "0" }) =>
    isNew(r) ? (
      <Input
        size="sm"
        type="number"
        step="0.01"
        className="!text-right"
        value={newValue}
        onChange={(e) => setNewValue(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
      />
    ) : (
      <Input
        key={r.id}
        size="sm"
        type="number"
        step="0.01"
        className="!text-right"
        defaultValue={r.totalAmount || ""}
        placeholder="0"
        onBlur={(e) => onBlur(r, e.target.value)}
      />
    );

  const descCell = (r, { newValue, setNewValue, onKeyDown, onBlur }) =>
    isNew(r) ? (
      <Input size="sm" value={newValue} onChange={(e) => setNewValue(e.target.value)} onKeyDown={onKeyDown} placeholder="Açıklama girip Enter'a basın..." />
    ) : (
      <Input key={r.id} size="sm" defaultValue={r.description} onBlur={(e) => onBlur(r, e.target.value)} placeholder="Açıklama..." />
    );

  const sefColumns = [
    noColumn(cashierPayments),
    {
      key: "description",
      dataIndex: "description",
      title: "Açıklama",
      render: (r) => descCell(r, { newValue: newDesc, setNewValue: setNewDesc, onKeyDown: handleKeyDown, onBlur: handleUpdatePaymentDescription }),
    },
    {
      key: "amount",
      dataIndex: "totalAmount",
      title: "Tutar (₺)",
      width: 150,
      render: (r) => amountCell(r, { newValue: newAmount, setNewValue: setNewAmount, onKeyDown: handleKeyDown, onBlur: handleUpdatePaymentAmount }),
    },
    deleteColumn(handleDeletePayment),
  ];

  const coinColumns = [
    noColumn(coinSettlements),
    {
      key: "amount",
      dataIndex: "totalAmount",
      title: "Tutar (₺)",
      render: (r) =>
        amountCell(r, {
          newValue: newCoinAmount,
          setNewValue: setNewCoinAmount,
          onKeyDown: handleCoinKeyDown,
          onBlur: handleUpdateCoinAmount,
          placeholder: "Miktar girip Enter'a basın...",
        }),
    },
    deleteColumn(handleDeleteCoin),
  ];

  const debtColumns = [
    noColumn(debtInformations),
    {
      key: "description",
      dataIndex: "description",
      title: "Açıklama",
      render: (r) => descCell(r, { newValue: newDebtDesc, setNewValue: setNewDebtDesc, onKeyDown: handleDebtKeyDown, onBlur: handleUpdateDebtDescription }),
    },
    {
      key: "amount",
      dataIndex: "totalAmount",
      title: "Tutar (₺)",
      width: 150,
      render: (r) => amountCell(r, { newValue: newDebtAmount, setNewValue: setNewDebtAmount, onKeyDown: handleDebtKeyDown, onBlur: handleUpdateDebtAmount }),
    },
    deleteColumn(handleDeleteDebt),
  ];

  const totalBar = (total) => (
    <div className="mt-3 flex items-center justify-end gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm">
      <span className="text-slate-600">Toplam:</span>
      <b className="text-slate-900">{fmt(total)} ₺</b>
    </div>
  );

  const TABS = [
    { key: "sef", title: "Kasa Şefi Ödemeleri", count: cashierPayments.length, rows: cashierPayments, columns: sefColumns, total: sefTotal },
    { key: "bozuk", title: "Bozuk Para Alımı", count: coinSettlements.length, rows: coinSettlements, columns: coinColumns, total: coinTotal },
    { key: "gecis", title: "Geçiş Borç", count: debtInformations.length, rows: debtInformations, columns: debtColumns, total: debtTotal },
  ];

  return (
    <Modal title={`Ödemeler — ${date}`} onClose={onClose} width={720}>
      <div className="min-h-[40vh]">
        <Tab
          variant="filled"
          className="!w-full"
          activeKey={activeTab}
          onChange={setActiveTab}
          items={TABS.map((t) => ({
            key: t.key,
            title: (
              <span className="flex items-center gap-1.5">
                {t.title}
                <Badge size="xs" radius="full" variant="soft" color={activeTab === t.key ? "primary" : "default"}>
                  {t.count}
                </Badge>
              </span>
            ),
            content:
              activeTab === t.key ? (
                <div>
                  <Table
                    size="sm"
                    uniqueKey="id"
                    columns={t.columns}
                    dataSource={withNewRow(t.rows)}
                    onRow={(r) => ({ className: isNew(r) ? "bg-blue-50/40" : undefined })}
                  />
                  {t.rows.length > 0 && totalBar(t.total)}
                </div>
              ) : null,
          }))}
        />
      </div>
    </Modal>
  );
};

/* ============================================================
   ExportModal — Client-side Z-Report PDF Export Modal
============================================================ */
const ExportModal = ({ onClose, initialDate }) => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const defs = useSelector((s) => s.zRaporDefs);

  const [exportDate, setExportDate] = useState(initialDate);
  const [exportFileName, setExportFileName] = useState("");
  const [exportReports, setExportReports] = useState([]);
  const [checkedReportIds, setCheckedReportIds] = useState([]);
  const [localPayments, setLocalPayments] = useState({ sef: [], coin: [], debt: [] });
  const [loading, setLoading] = useState(false);

  // Auto-update file name when date changes
  useEffect(() => {
    if (exportDate) {
      const [y, m, d] = exportDate.split("-");
      setExportFileName(`${d}.${m}.${y}.pdf`);
    }
  }, [exportDate]);

  // Fetch closed reports and payments for selected date
  useEffect(() => {
    if (user?.branchId && exportDate) {
      setLoading(true);
      Promise.all([
        zreportapi.get(`ZReports/branch/${user.branchId}`, {
          params: { page: 1, size: 100, reportDate: exportDate }
        }),
        zreportapi.get(`DataCashierPayments/branch/${user.branchId}/transaction-date/${exportDate}`, {
          params: { page: 1, size: 100 }
        }),
        zreportapi.get(`DataCoinSettlements/branch/${user.branchId}/transaction-date/${exportDate}`, {
          params: { page: 1, size: 100 }
        }),
        zreportapi.get(`DataDebtInformations/branch/${user.branchId}/transaction-date/${exportDate}`, {
          params: { page: 1, size: 100 }
        })
      ]).then(([repRes, payRes, coinRes, debtRes]) => {
        const items = repRes.data?.items || [];
        const mapped = items.map((item) => ({
          id: item.id,
          date: item.reportDate,
          kasiyerId: item.cashierId,
          kasaId: item.caseId,
          raporNo: item.no,
          toplamTutar: Number(item.totalAmount || 0),
          nakit: Number(item.cashAmount || 0),
          hediyeKart: Number(item.pointAmount || 0),
          musteriSayisi: Number(item.numberOfCustomer || 0),
          status: "closed",
          visa: (item.entryBankItems || []).map((e) => ({
            id: e.id,
            bankaId: e.definitionBankId,
            tutar: Number(e.amount || 0)
          })),
          giderler: (item.entryExpenseItems || []).map((e) => ({
            id: e.id,
            kategoriId: e.definitionExpenseId,
            tutar: Number(e.amount || 0)
          })),
          borclar: (item.entryDebtItems || []).map((e) => ({
            id: e.id,
            musteriId: e.definitionDebtId,
            tutar: Number(e.amount || 0)
          }))
        }));
        setExportReports(mapped);
        setCheckedReportIds(mapped.map((r) => r.id));
        setLocalPayments({
          sef: payRes.data?.items || [],
          coin: coinRes.data?.items || [],
          debt: debtRes.data?.items || []
        });
      }).catch((err) => {
        console.error("Failed to load export data:", err);
      }).finally(() => {
        setLoading(false);
      });
    }
  }, [exportDate, user?.branchId]);

  const selectedReports = useMemo(() => {
    return exportReports.filter((r) => checkedReportIds.includes(r.id));
  }, [exportReports, checkedReportIds]);

  const handleToggleRow = (id) => {
    setCheckedReportIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleToggleAll = () => {
    if (checkedReportIds.length === exportReports.length) {
      setCheckedReportIds([]);
    } else {
      setCheckedReportIds(exportReports.map((r) => r.id));
    }
  };

  const getTurkishLongDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "long",
      year: "numeric",
      weekday: "long"
    });
  };

  const getTurkishDayName = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("tr-TR", { weekday: "long" });
  };

  const getTurkishShortDate = (dateStr) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-");
    return `${d}.${m}.${y}`;
  };

  const handleExportPDF = () => {
    if (selectedReports.length === 0) {
      goldAlert.fire({
        icon: "warning",
        title: "Aktarılacak Rapor Yok",
        text: "Seçilen tarihte aktarılacak kapalı Z-Raporu bulunmamaktadır."
      });
      return;
    }

    const kasalar = defs.kasalar || [];
    const kasiyerler = defs.kasiyerler || [];
    const bankalar = defs.bankalar || [];
    const giderKategoriler = defs.giderKategoriler || [];
    const borcMusteriler = defs.borcMusteriler || [];

    const totalSales = selectedReports.reduce((s, r) => s + Number(r.toplamTutar || 0), 0);
    const totalCustomers = selectedReports.reduce((s, r) => s + Number(r.musteriSayisi || 0), 0);
    const averageBasket = totalCustomers > 0 ? (totalSales / totalCustomers) : 0;
    const caseCount = selectedReports.length;
    const totalCash = selectedReports.reduce((s, r) => s + Number(r.nakit || 0), 0);
    const totalPoints = selectedReports.reduce((s, r) => s + Number(r.hediyeKart || 0), 0);

    const sefTotal = localPayments.sef.reduce((s, p) => s + Number(p.totalAmount || 0), 0);
    const coinTotal = localPayments.coin.reduce((s, p) => s + Number(p.totalAmount || 0), 0);
    const debtTotal = localPayments.debt.reduce((s, p) => s + Number(p.totalAmount || 0), 0);
    const netTotal = sefTotal - coinTotal - debtTotal;

    const reportChunks = [];
    const chunkSize = 9;
    for (let i = 0; i < selectedReports.length; i += chunkSize) {
      reportChunks.push(selectedReports.slice(i, i + chunkSize));
    }

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>${exportFileName.replace(".pdf", "")}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&display=swap');
            @page {
              size: A4;
              margin: 0;
            }
            body {
              font-family: 'Outfit', 'Inter', sans-serif;
              color: #212529;
              margin: 0;
              padding: 8mm 12mm 8mm 12mm;
              font-size: 8.5px;
              line-height: 1.35;
              background-color: #fff;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            
            /* Main Header Panel */
            .main-report-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 8px;
            }
            .header-left {
              display: flex;
              flex-direction: column;
            }
            .main-title {
              font-size: 24px;
              font-weight: 800;
              color: #212529;
              letter-spacing: -0.5px;
              margin-bottom: 2px;
            }
            .main-subtitle {
              font-size: 9px;
              color: #8e8e93;
              font-weight: 500;
            }
            .header-right {
              text-align: right;
            }
            .header-right-date {
              font-size: 12px;
              font-weight: 700;
              color: #212529;
            }
            .header-right-day {
              font-size: 9px;
              color: #8e8e93;
              font-weight: 500;
            }

            /* 6-Column KPI Bar */
            .kpi-container {
              background-color: #fff;
              border-top: 1.5px solid #0f2c59;
              border-bottom: 1.5px solid #0f2c59;
              padding: 8px 0;
              display: grid;
              grid-template-columns: repeat(6, 1fr);
              margin-bottom: 14px;
            }
            .kpi-item {
              display: flex;
              flex-direction: column;
              align-items: center;
              border-right: 1px solid #dee2e6;
            }
            .kpi-item:last-child {
              border-right: none;
            }
            .kpi-lbl {
              font-size: 8px;
              font-weight: 700;
              color: #8e8e93;
              text-transform: uppercase;
              letter-spacing: 0.3px;
              margin-bottom: 4px;
            }
            .kpi-val {
              font-size: 13.5px;
              font-weight: 800;
              color: #212529;
            }

            /* Section Title */
            .section-title {
              font-size: 9.5px;
              font-weight: 800;
              color: #0f2c59;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              margin-bottom: 10px;
            }

            /* 3x3 Cards Panel Grid - Collapsed Borders */
            .cards-container-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 0;
              border-top: 1px solid #d2d2d7;
              border-left: 1px solid #d2d2d7;
              background-color: #fff;
              page-break-inside: avoid;
              margin-bottom: 15px;
            }

            /* Report Card styling - Stretch naturally to match sibling height */
            .report-card {
              border-right: 1px solid #d2d2d7;
              border-bottom: 1px solid #d2d2d7;
              background-color: #fff;
              display: flex;
              flex-direction: column;
              box-sizing: border-box;
              font-size: 8px;
            }
            .card-header {
              background-color: #f8f9fa;
              border-bottom: 1px solid #d2d2d7;
              padding: 4px 8px;
              display: flex;
              justify-content: space-between;
              align-items: center;
              font-weight: 700;
              font-size: 9px;
            }
            .card-header-title {
              color: #212529;
            }
            .card-header-badge {
              font-weight: 800;
            }
            .card-header-badge.fazla {
              color: #2e7d32;
            }
            .card-header-badge.acik {
              color: #c62828;
            }
            .card-header-badge.esit {
              color: #6c757d;
            }
            .card-body {
              padding: 5px 8px 0 8px;
              display: flex;
              flex-direction: column;
              gap: 2.5px;
              flex: 1;
            }
            .card-row {
              display: flex;
              justify-content: space-between;
              font-size: 8px;
            }
            .card-row-label {
              color: #8e8e93;
              font-weight: 500;
            }
            .card-row-val {
              font-weight: 700;
              color: #212529;
            }
            .card-divider-dashed {
              border-top: 1px dashed #d2d2d7;
              margin: 3px 0;
            }
            .card-section-title {
              font-size: 7px;
              font-weight: 800;
              color: #8e8e93;
              text-transform: uppercase;
              margin-bottom: 1px;
              letter-spacing: 0.3px;
            }
            .card-sublist-item {
              display: flex;
              justify-content: space-between;
              margin-bottom: 0.5px;
              font-size: 7.5px;
            }
            .card-sublist-label {
              color: #6c757d;
              font-weight: 500;
            }
            .card-sublist-val {
              font-weight: 700;
              color: #212529;
            }
            .card-total-row {
              background-color: #000;
              color: #fff;
              padding: 4px 8px;
              display: flex;
              justify-content: space-between;
              align-items: center;
              font-weight: 800;
              font-size: 9px;
              margin-top: auto;
              border-top: 1px solid #d2d2d7;
            }
            
            /* Center Separator Title */
            .center-separator-title {
              font-size: 11px;
              font-weight: 800;
              color: #0f2c59;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              margin: 25px 0 15px 0;
              text-align: center;
              border-bottom: 1.5px solid #0f2c59;
              padding-bottom: 4px;
            }

            /* 3-Column Tables Grid */
            .tables-container-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 15px;
              margin-bottom: 25px;
              page-break-inside: avoid;
            }
            .table-card {
              border: 1px solid #dee2e6;
              border-radius: 4px;
              overflow: hidden;
              background-color: #fff;
              display: flex;
              flex-direction: column;
            }
            .table-card-header {
              color: #fff;
              padding: 8px 10px;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .table-card-header.sef {
              background-color: #0f2c59;
            }
            .table-card-header.coin {
              background-color: #0f5a3e;
            }
            .table-card-header.debt {
              background-color: #a31d1d;
            }
            .table-title-area {
              display: flex;
              flex-direction: column;
            }
            .table-title-text {
              font-size: 9.5px;
              font-weight: 800;
              letter-spacing: 0.3px;
            }
            .table-title-sub {
              font-size: 7.5px;
              opacity: 0.8;
              margin-top: 1px;
              font-weight: 500;
            }
            .table-title-total {
              font-size: 10px;
              font-weight: 800;
            }
            .operasyon-table {
              width: 100%;
              border-collapse: collapse;
              font-size: 8.5px;
            }
            .operasyon-table th {
              background-color: #f8f9fa;
              border-bottom: 1px solid #dee2e6;
              color: #495057;
              font-weight: 700;
              padding: 4px 6px;
              text-align: left;
              text-transform: uppercase;
              font-size: 7.5px;
            }
            .operasyon-table td {
              border-bottom: 1px solid #f1f3f5;
              padding: 5px 6px;
              color: #212529;
            }

            /* Bottom Calculations Banner */
            .bottom-calculations-banner {
              background-color: #0f2c59;
              color: #fff;
              border-radius: 4px;
              padding: 12px 20px;
              display: flex;
              justify-content: space-between;
              align-items: center;
              font-family: 'Outfit', sans-serif;
              page-break-inside: avoid;
              margin-top: 30px;
            }
            .banner-block {
              display: flex;
              flex-direction: column;
              align-items: center;
              flex: 1;
            }
            .banner-lbl {
              font-size: 7.5px;
              font-weight: 700;
              color: rgba(255,255,255,0.7);
              text-transform: uppercase;
              letter-spacing: 0.3px;
              margin-bottom: 3px;
            }
            .banner-val {
              font-size: 13.5px;
              font-weight: 800;
            }
            .banner-operator {
              font-size: 18px;
              font-weight: 700;
              color: rgba(255,255,255,0.5);
              padding: 0 10px;
            }
            .banner-net-total-box {
              background-color: #1a4f8f;
              border: 1.5px solid #22c55e;
              border-radius: 4px;
              padding: 4px 12px;
              display: flex;
              flex-direction: column;
              align-items: center;
              min-width: 80px;
            }
            .banner-net-total-val {
              font-size: 13.5px;
              font-weight: 900;
              color: #22c55e;
            }

            .page-break {
              page-break-before: always;
            }
            .table-container {
              margin-top: 20px;
              page-break-inside: avoid;
            }
            .operasyon-table {
              width: 100%;
              border-collapse: collapse;
              font-size: 10px;
              margin-top: 6px;
            }
            .operasyon-table th, .operasyon-table td {
              border: 1px solid #e5e5e7;
              padding: 5px 8px;
              text-align: left;
            }
            .operasyon-table th {
              background-color: #fafafa;
              font-weight: 700;
              color: #86868b;
            }
            .summary-math-container {
              display: flex;
              flex-direction: column;
              align-items: flex-end;
              margin-top: 30px;
              font-size: 11px;
              page-break-inside: avoid;
            }
            .math-row {
              display: flex;
              justify-content: space-between;
              width: 280px;
              margin-bottom: 5px;
            }
            .math-label {
              color: #86868b;
              font-weight: 700;
              letter-spacing: 0.3px;
            }
            .math-val {
              font-weight: 700;
            }
            .math-net-row {
              display: flex;
              justify-content: space-between;
              width: 280px;
              border-top: 2px solid #1d1d1f;
              padding-top: 8px;
              margin-top: 8px;
              font-size: 13px;
              font-weight: 800;
            }
            .page-break {
              page-break-before: always;
            }
          </style>
        </head>
        <body>
          ${reportChunks.map((chunk, chunkIdx) => {
            const isFirstPage = chunkIdx === 0;
            
            let headerHTML = "";
            if (isFirstPage) {
              headerHTML = `
                <div class="main-report-header">
                  <div class="header-left">
                    <span class="main-title">${exportFileName.replace(/\.[^/.]+$/, "")}</span>
                    <span class="main-subtitle">${caseCount} kasa · Oluşturulma: ${getTurkishLongDate(exportDate)}</span>
                  </div>
                  <div class="header-right">
                    <div class="header-right-date">${getTurkishLongDate(exportDate).replace(getTurkishDayName(exportDate), "")}</div>
                    <div class="header-right-day">${getTurkishDayName(exportDate)}</div>
                  </div>
                </div>

                <div class="kpi-container">
                  <div class="kpi-item">
                    <span class="kpi-lbl">TOPLAM SATIŞ</span>
                    <span class="kpi-val">${fmt(totalSales)} ₺</span>
                  </div>
                  <div class="kpi-item">
                    <span class="kpi-lbl">MÜŞTERİ SAYISI</span>
                    <span class="kpi-val">${totalCustomers}</span>
                  </div>
                  <div class="kpi-item">
                    <span class="kpi-lbl">SEPET ORTALAMASI</span>
                    <span class="kpi-val">${fmt(averageBasket)} ₺</span>
                  </div>
                  <div class="kpi-item">
                    <span class="kpi-lbl">KASA SAYISI</span>
                    <span class="kpi-val">${caseCount}</span>
                  </div>
                  <div class="kpi-item">
                    <span class="kpi-lbl">TOPLAM NAKİT</span>
                    <span class="kpi-val">${fmt(totalCash)} ₺</span>
                  </div>
                  <div class="kpi-item">
                    <span class="kpi-lbl">TOPLAM PUAN</span>
                    <span class="kpi-val">${fmt(totalPoints)} ₺</span>
                  </div>
                </div>
              `;
            } else {
              headerHTML = `
                <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1.5px solid #0f2c59; padding-bottom: 6px; margin-bottom: 15px;">
                  <div style="font-size: 9.5px; font-weight: 800; text-transform: uppercase; color: #0f2c59; letter-spacing: 0.5px;">
                    Z-RAPORU GÜNLÜK HESAP AKTARIMI — Sayfa ${chunkIdx + 1}
                  </div>
                  <div style="font-size: 9.5px; font-weight: 700; color: #6c757d;">
                    ${exportFileName.replace(/\.[^/.]+$/, "")}
                  </div>
                </div>
              `;
            }

            const cardsHTML = chunk.map((r) => {
              const cashier = (kasiyerler || []).find((x) => x.id === r.kasiyerId);
              const cashierNameStr = cashier ? `${cashier.name} ${cashier.surname}`.toUpperCase() : "—";
              const cs = (kasalar || []).find((x) => x.id === r.kasaId);
              const caseNo = cs ? cs.no || cs.title?.split(" ")[0] || "—" : "—";

              const tToplam = Number(r.nakit || 0) + Number(r.hediyeKart || 0) +
                r.visa.reduce((s, v) => s + Number(v.tutar || 0), 0) +
                r.borclar.reduce((s, b) => s + Number(b.tutar || 0), 0) +
                r.giderler.reduce((s, g) => s + Number(g.tutar || 0), 0);
              const diff = tToplam - Number(r.toplamTutar || 0);
              
              let statusText = "KASA EŞİT";
              let badgeClass = "esit";
              if (diff < -0.01) {
                statusText = `AÇIK: ${fmt(Math.abs(diff))}`;
                badgeClass = "acik";
              } else if (diff > 0.01) {
                statusText = `FAZLA: ${fmt(diff)}`;
                badgeClass = "fazla";
              }

              const visaRows = r.visa.map((v) => {
                const bank = bankalar.find((x) => x.id === v.bankaId);
                const bankName = bank ? bank.title || bank.name : "BANKA";
                return `
                  <div class="card-sublist-item">
                    <span class="card-sublist-label">${bankName.toUpperCase()}</span>
                    <span class="card-sublist-val">${fmt(v.tutar)}</span>
                  </div>
                `;
              }).join("");

              const giderRows = r.giderler.map((g) => {
                const cat = giderKategoriler.find((x) => x.id === g.kategoriId);
                const catName = cat ? cat.title || cat.name : "GİDER";
                return `
                  <div class="card-sublist-item">
                    <span class="card-sublist-label">${catName.toUpperCase()}</span>
                    <span class="card-sublist-val">${fmt(g.tutar)}</span>
                  </div>
                `;
              }).join("");

              const borcRows = r.borclar.map((b) => {
                const cust = borcMusteriler.find((x) => x.id === b.musteriId);
                const custName = cust ? cust.title || cust.name : "MÜŞTERİ";
                return `
                  <div class="card-sublist-item">
                    <span class="card-sublist-label">${custName.toUpperCase()}</span>
                    <span class="card-sublist-val">${fmt(b.tutar)}</span>
                  </div>
                `;
              }).join("");

              return `
                <div class="report-card">
                  <div class="card-header">
                    <span class="card-header-title">${cs ? cs.title || cs.name || "KASA" : "KASA"}</span>
                    <span class="card-header-badge ${badgeClass}">${statusText}</span>
                  </div>
                  <div class="card-body">
                    <div class="card-row">
                      <span class="card-row-label">Rapor No</span>
                      <span class="card-row-val">${r.raporNo || "—"}</span>
                    </div>
                    <div class="card-row">
                      <span class="card-row-label">Müşteri Sayısı</span>
                      <span class="card-row-val">${r.musteriSayisi || 0}</span>
                    </div>
                    <div class="card-row">
                      <span class="card-row-label">Sorumlu</span>
                      <span class="card-row-val">${cashierNameStr}</span>
                    </div>
                    <div class="card-row">
                      <span class="card-row-label">Nakit</span>
                      <span class="card-row-val">${fmt(r.nakit)}</span>
                    </div>
                    <div class="card-row">
                      <span class="card-row-label">Puan</span>
                      <span class="card-row-val">${fmt(r.hediyeKart)}</span>
                    </div>
                    
                    <div class="card-divider-dashed"></div>

                    ${visaRows || ""}

                    <div class="card-section-title" style="margin-top: 4px;">GİDERLER</div>
                    ${giderRows ? giderRows : `
                      <div class="card-sublist-item">
                        <span class="card-sublist-label">—</span>
                        <span class="card-sublist-val"></span>
                      </div>
                    `}

                    <div class="card-section-title" style="margin-top: 4px;">BORÇLAR</div>
                    ${borcRows ? borcRows : `
                      <div class="card-sublist-item">
                        <span class="card-sublist-label">—</span>
                        <span class="card-sublist-val"></span>
                      </div>
                    `}
                  </div>
                  <div class="card-total-row">
                    <span>Z-TOPLAM</span>
                    <span>${fmt(r.toplamTutar)} ₺</span>
                  </div>
                </div>
              `;
            }).join("");

            return `
              ${headerHTML}
              ${isFirstPage ? '<div class="section-title">KASA OPERASYON DETAYLARI</div>' : ''}
              <div class="cards-container-grid">
                ${cardsHTML}
              </div>
              ${chunkIdx < reportChunks.length - 1 ? '<div class="page-break"></div>' : ''}
            `;
          }).join("")}

          <div class="center-separator-title">OPERASYON DETAYLARI</div>
          
          <div class="tables-container-grid">
            <!-- Table 1: Kasa Şef Ödemeleri -->
            <div class="table-card">
              <div class="table-card-header sef">
                <div class="table-title-area">
                  <span class="table-title-text">Kasa Şef Ödemeleri</span>
                  <span class="table-title-sub">${localPayments.sef.length} kayıt</span>
                </div>
                <span class="table-title-total">${fmt(sefTotal)} ₺</span>
              </div>
              <table class="operasyon-table">
                <thead>
                  <tr>
                    <th>Açıklama</th>
                    <th style="width: 70px; text-align: right;">Tutar</th>
                  </tr>
                </thead>
                <tbody>
                  ${localPayments.sef.length === 0 ? `
                    <tr>
                      <td colspan="2" style="text-align: center; color: #86868b; font-style: italic; padding: 12px 0;">Kayıt bulunamadı</td>
                    </tr>
                  ` : localPayments.sef.map((p) => `
                    <tr>
                      <td>${(p.description || 'Kasa Şef Ödemesi').toUpperCase()}</td>
                      <td style="text-align: right; font-weight: 700;">${fmt(p.totalAmount)} ₺</td>
                    </tr>
                  `).join("")}
                </tbody>
              </table>
            </div>

            <!-- Table 2: Bozuk Para Alımları -->
            <div class="table-card">
              <div class="table-card-header coin">
                <div class="table-title-area">
                  <span class="table-title-text">Bozuk Para Alımları</span>
                  <span class="table-title-sub">${localPayments.coin.length} kayıt</span>
                </div>
                <span class="table-title-total">${fmt(coinTotal)} ₺</span>
              </div>
              <table class="operasyon-table">
                <thead>
                  <tr>
                    <th>Açıklama</th>
                    <th style="width: 70px; text-align: right;">Tutar</th>
                  </tr>
                </thead>
                <tbody>
                  ${localPayments.coin.length === 0 ? `
                    <tr>
                      <td colspan="2" style="text-align: center; color: #86868b; font-style: italic; padding: 12px 0;">Kayıt bulunamadı</td>
                    </tr>
                  ` : localPayments.coin.map((p) => `
                    <tr>
                      <td>BOZUK PARA ALIMI</td>
                      <td style="text-align: right; font-weight: 700;">${fmt(p.totalAmount)} ₺</td>
                    </tr>
                  `).join("")}
                </tbody>
              </table>
            </div>

            <!-- Table 3: Geçmiş Borçlar -->
            <div class="table-card">
              <div class="table-card-header debt">
                <div class="table-title-area">
                  <span class="table-title-text">Geçmiş Borçlar</span>
                  <span class="table-title-sub">${localPayments.debt.length} kayıt</span>
                </div>
                <span class="table-title-total">${fmt(debtTotal)} ₺</span>
              </div>
              <table class="operasyon-table">
                <thead>
                  <tr>
                    <th>Açıklama</th>
                    <th style="width: 70px; text-align: right;">Tutar</th>
                  </tr>
                </thead>
                <tbody>
                  ${localPayments.debt.length === 0 ? `
                    <tr>
                      <td colspan="2" style="text-align: center; color: #86868b; font-style: italic; padding: 12px 0;">Kayıt bulunamadı</td>
                    </tr>
                  ` : localPayments.debt.map((p) => `
                    <tr>
                      <td>${(p.description || 'Geçmiş Borç').toUpperCase()}</td>
                      <td style="text-align: right; font-weight: 700;">${fmt(p.totalAmount)} ₺</td>
                    </tr>
                  `).join("")}
                </tbody>
              </table>
            </div>
          </div>

          <div class="bottom-calculations-banner">
            <div class="banner-block">
              <span class="banner-lbl">KASA ŞEF ÖDEMELERİ</span>
              <span class="banner-val">${fmt(sefTotal)} ₺</span>
            </div>
            <span class="banner-operator">−</span>
            <div class="banner-block">
              <span class="banner-lbl">BOZUK PARA ALIMLARI</span>
              <span class="banner-val">${fmt(coinTotal)} ₺</span>
            </div>
            <span class="banner-operator">−</span>
            <div class="banner-block">
              <span class="banner-lbl">GEÇMİŞ BORÇLAR</span>
              <span class="banner-val">${fmt(debtTotal)} ₺</span>
            </div>
            <span class="banner-operator">=</span>
            <div class="banner-block">
              <div class="banner-net-total-box">
                <span class="banner-lbl" style="color: rgba(255,255,255,0.8); margin-bottom: 2px;">NET TOPLAM</span>
                <span class="banner-net-total-val">${fmt(netTotal)} ₺</span>
              </div>
            </div>
          </div>

          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const previewSales = useMemo(() => selectedReports.reduce((s, r) => s + Number(r.toplamTutar || 0), 0), [selectedReports]);
  const previewCash = useMemo(() => selectedReports.reduce((s, r) => s + Number(r.nakit || 0), 0), [selectedReports]);
  const previewPoints = useMemo(() => selectedReports.reduce((s, r) => s + Number(r.hediyeKart || 0), 0), [selectedReports]);

  const sefTotal = useMemo(() => localPayments.sef.reduce((s, p) => s + Number(p.totalAmount || 0), 0), [localPayments.sef]);
  const coinTotal = useMemo(() => localPayments.coin.reduce((s, p) => s + Number(p.totalAmount || 0), 0), [localPayments.coin]);
  const debtTotal = useMemo(() => localPayments.debt.reduce((s, p) => s + Number(p.totalAmount || 0), 0), [localPayments.debt]);

  return (
    <Drawer
      isOpen
      onClose={onClose}
      position="right"
      size={95}
      variant="blur"
      className="!w-[480px] !max-w-[95vw]"
      title={
        <span className="flex flex-col">
          <span className="flex items-center gap-2 text-sm font-bold">
            📤 Hesapları Aktar (PDF)
            <Badge size="xs" radius="full" variant="soft" color="primary">
              {exportReports.length} Kasa
            </Badge>
          </span>
          <span className="text-[11px] font-normal text-slate-500">Aktarılacak kasaları seçip PDF dosyasını indirebilirsiniz.</span>
        </span>
      }
      footer={
        <div className="flex w-full gap-2">
          <Button variant="outline" color="default" className="!w-full" onClick={onClose}>
            Vazgeç
          </Button>
          <Button className="!w-full" onClick={handleExportPDF} disabled={loading || selectedReports.length === 0}>
            PDF Olarak Aktar ({selectedReports.length})
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="flex flex-col gap-1">
            <FieldLabel>Tarih Seçimi</FieldLabel>
            <Input size="sm" type="date" value={exportDate} onChange={(e) => setExportDate(e.target.value)} disabled={loading} />
          </div>
          <div className="flex flex-col gap-1">
            <FieldLabel>Dosya Adı</FieldLabel>
            <Input
              size="sm"
              value={exportFileName}
              onChange={(e) => setExportFileName(e.target.value)}
              placeholder="Örn: 17.05.2026.pdf"
              disabled={loading}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between px-1">
            <span className="text-[12px] font-bold text-slate-700">📋 Rapor Listesi</span>
            {exportReports.length > 0 && (
              <Button size="xs" variant="link" color="primary" onClick={handleToggleAll}>
                {checkedReportIds.length === exportReports.length ? "Tüm Seçimleri Kaldır" : "Tümünü Seç"}
              </Button>
            )}
          </div>

          <div className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-2">
            {loading ? (
              <div className="flex justify-center py-8">
                <Spinner size={24} label="Veriler yükleniyor..." />
              </div>
            ) : exportReports.length === 0 ? (
              <Empty size="sm" title="Rapor yok" description="Seçilen tarihte kapalı Z-Raporu bulunamadı." />
            ) : (
              exportReports.map((r) => {
                const cashier = (defs.kasiyerler || []).find((x) => x.id === r.kasiyerId);
                const cashierName = cashier ? `${cashier.name} ${cashier.surname}` : "Bilinmeyen Kasiyer";
                const cs = (defs.kasalar || []).find((x) => x.id === r.kasaId);
                const caseName = cs ? cs.title || `${cs.no} - ${cs.name}` : "Bilinmeyen Kasa";
                const isChecked = checkedReportIds.includes(r.id);

                return (
                  <div
                    key={r.id}
                    className={`flex items-center gap-3 rounded-lg border p-2.5 transition-colors ${
                      isChecked ? "border-blue-200 bg-blue-50/40" : "border-slate-100 bg-white hover:bg-slate-50"
                    }`}
                  >
                    <Checkbox checked={isChecked} onChange={() => handleToggleRow(r.id)} />
                    <div className="flex flex-1 items-center justify-between">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[12px] font-bold text-slate-800">{caseName}</span>
                        <span className="text-[10.5px] text-slate-500">Kasiyer: {cashierName}</span>
                      </div>
                      <div className="flex shrink-0 flex-col items-end text-right">
                        <span className="text-[12.5px] font-extrabold text-blue-600">{fmt(r.toplamTutar)} ₺</span>
                        <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Z-Toplam</span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </Drawer>
  );
};

/* ============================================================
   CashierCalculationDrawer — Cashier-based calculations and filters
============================================================ */
const CashierCalculationDrawer = ({ onClose }) => {
  const { user } = useAuth();
  const todayStr = new Date().toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [amountCriteria, setAmountCriteria] = useState("0");
  const [filterType, setFilterType] = useState(0);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);

  const getTurkishShortDate = (dateStr) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("T")[0].split("-");
    return `${d}.${m}.${y}`;
  };

  const getCalculationTypeBadge = (type) => {
    const [color, label] = { 1: ["success", "KASA EŞİT"], 2: ["danger", "KASA AÇIĞI"], 3: ["warning", "KASA FAZLASI"] }[type] || ["primary", "TÜMÜ"];
    return (
      <Badge size="xs" variant="soft" color={color}>
        {label}
      </Badge>
    );
  };

  const handleQuery = () => {
    if (!user?.branchId) return;
    setLoading(true);
    zreportapi
      .get("ZReports/cashier-based-cash-calculation", {
        params: {
          branchId: user.branchId,
          startDate,
          endDate,
          amountCriteria: Number(amountCriteria) || 0,
          filterType: Number(filterType),
        },
      })
      .then((res) => {
        setResults(res.data || []);
      })
      .catch((err) => {
        console.error("Failed cashier calculation query:", err);
        goldAlert.fire({
          icon: "error",
          title: "Sorgulama Başarısız",
          text: "Kasiyer bazlı aktarım sorgusu çalıştırılırken bir hata oluştu.",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleExportExcel = () => {
    if (results.length === 0) return;
    
    // Headers
    let csvContent = "Kasiyer;Tarih;Kasa;Durum;Tutar\n";
    
    results.forEach((c) => {
      const cashierFullName = `${c.cashierName} ${c.cashierSurname}`;
      (c.items || []).forEach((item) => {
        const dateStr = getTurkishShortDate(item.reportDate);
        let statusStr = "Tümü";
        if (item.calculationType === 1) statusStr = "Kasa Eşit";
        else if (item.calculationType === 2) statusStr = "Kasa Açığı";
        else if (item.calculationType === 3) statusStr = "Kasa Fazlası";
        
        const prefix = item.calculationType === 2 ? "-" : item.calculationType === 3 ? "+" : "";
        const formattedAmount = `${prefix}${item.amount}`.replace(".", ",");
        
        csvContent += `${cashierFullName};${dateStr};${item.caseTitle};${statusStr};${formattedAmount}\n`;
      });
    });
    
    // Download file with UTF-8 BOM so Excel opens it with correct Turkish chars
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Kasiyer_Bazli_Hesap_Aktarimi_${startDate}_${endDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrintPDF = () => {
    if (results.length === 0) return;

    const printFrame = document.createElement("iframe");
    printFrame.style.position = "fixed";
    printFrame.style.right = "0";
    printFrame.style.bottom = "0";
    printFrame.style.width = "0";
    printFrame.style.height = "0";
    printFrame.style.border = "0";
    document.body.appendChild(printFrame);

    const doc = printFrame.contentWindow.document;

    let listHTML = "";
    results.forEach((c) => {
      let rowsHTML = "";
      (c.items || []).forEach((item) => {
        const isShortage = item.calculationType === 2;
        const isExcess = item.calculationType === 3;
        const statusLabel = isShortage ? "KASA AÇIĞI" : isExcess ? "KASA FAZLASI" : "KASA EŞİT";
        const amountSign = isShortage ? "-" : isExcess ? "+" : "";
        
        rowsHTML += `
          <tr class="item-row">
            <td class="cell font-mono">${getTurkishShortDate(item.reportDate)}</td>
            <td class="cell font-bold">${item.caseTitle}</td>
            <td class="cell text-center">
              <span class="badge ${isShortage ? "badge-shortage" : isExcess ? "badge-excess" : "badge-equal"}">
                ${statusLabel}
              </span>
            </td>
            <td class="cell text-right ${isShortage ? "color-shortage" : isExcess ? "color-excess" : ""}">
              ${amountSign}${fmt(item.amount)} ₺
            </td>
          </tr>
        `;
      });

      listHTML += `
        <div class="cashier-card">
          <div class="cashier-card-header">
            <div class="cashier-profile">
              <span class="profile-initials">
                ${(c.cashierName?.[0] || "").toUpperCase()}${(c.cashierSurname?.[0] || "").toUpperCase()}
              </span>
              <span class="cashier-name">${c.cashierName} ${c.cashierSurname}</span>
            </div>
            <span class="cashier-meta">${c.items?.length || 0} İşlem</span>
          </div>
          <table class="report-table">
            <thead>
              <tr>
                <th class="th-cell">Tarih</th>
                <th class="th-cell">Kasa</th>
                <th class="th-cell text-center">Durum</th>
                <th class="th-cell text-right">Tutar/Fark</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHTML}
            </tbody>
          </table>
        </div>
      `;
    });

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Kasiyer Bazlı Hesap Aktarımı Raporu</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');
            @page {
              size: A4;
              margin: 0;
            }
            body {
              font-family: 'Outfit', sans-serif;
              color: #1d1d1f;
              margin: 0;
              padding: 10mm 15mm;
              background: #fff;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .report-header {
              border-bottom: 2px solid #0f2c59;
              padding-bottom: 8px;
              margin-bottom: 20px;
              display: flex;
              justify-content: space-between;
              align-items: flex-end;
            }
            .header-title {
              font-size: 16px;
              font-weight: 800;
              color: #0f2c59;
              margin: 0;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .header-subtitle {
              font-size: 10px;
              color: #86868b;
              margin-top: 3px;
            }
            .header-meta {
              text-align: right;
              font-size: 10px;
              color: #6c757d;
            }
            .cashier-card {
              border: 1px solid #e2e2e7;
              border-radius: 8px;
              margin-bottom: 15px;
              page-break-inside: avoid;
              overflow: hidden;
              background: #fff;
            }
            .cashier-card-header {
              background: #f5f5f7;
              padding: 8px 12px;
              display: flex;
              justify-content: space-between;
              align-items: center;
              border-bottom: 1px solid #e2e2e7;
            }
            .cashier-profile {
              display: flex;
              align-items: center;
              gap: 8px;
            }
            .profile-initials {
              width: 24px;
              height: 24px;
              border-radius: 50%;
              background: #0f2c59;
              color: #fff;
              display: flex;
              align-items: center;
              justify-content: center;
              font-weight: 700;
              font-size: 10px;
            }
            .cashier-name {
              font-size: 12px;
              font-weight: 700;
              color: #0f2c59;
            }
            .cashier-meta {
              font-size: 10px;
              background: #e8e8ed;
              color: #0f2c59;
              font-weight: 700;
              padding: 2px 8px;
              border-radius: 20px;
            }
            .report-table {
              width: 100%;
              border-collapse: collapse;
              font-size: 10px;
            }
            .th-cell {
              background: #fafafa;
              color: #6c757d;
              font-weight: 700;
              padding: 6px 12px;
              border-bottom: 1px solid #e2e2e7;
            }
            .cell {
              padding: 6px 12px;
              border-bottom: 1px solid #f5f5f7;
            }
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .font-mono { font-family: monospace; }
            .font-bold { font-weight: 700; }
            .color-shortage { color: #d92518; }
            .color-excess { color: #107c41; }
            .badge {
              font-size: 8px;
              font-weight: 800;
              padding: 2px 6px;
              border-radius: 4px;
              display: inline-block;
            }
            .badge-shortage {
              background: #fdf0ed;
              color: #d92518;
              border: 0.5px solid #fbc4b8;
            }
            .badge-excess {
              background: #edf7ed;
              color: #107c41;
              border: 0.5px solid #c2e0c2;
            }
            .badge-equal {
              background: #eaf6ff;
              color: #007aff;
              border: 0.5px solid #b3d7ff;
            }
          </style>
        </head>
        <body>
          <div class="report-header">
            <div>
              <h1 class="header-title">Kasiyer Bazlı Hesap Aktarımı Raporu</h1>
              <div class="header-subtitle">Tarih Aralığı: ${getTurkishShortDate(startDate)} — ${getTurkishShortDate(endDate)}</div>
            </div>
            <div class="header-meta">
              Oluşturulma Tarihi: ${getTurkishShortDate(todayStr)}<br/>
              Limit Kriteri: ${amountCriteria} ₺ üzeri farklar
            </div>
          </div>
          ${listHTML}
        </body>
      </html>
    `;

    doc.open();
    doc.write(htmlContent);
    doc.close();

    printFrame.onload = () => {
      setTimeout(() => {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
        setTimeout(() => {
          document.body.removeChild(printFrame);
        }, 1000);
      }, 500);
    };
  };

  // Run query on load
  useEffect(() => {
    handleQuery();
  }, []);

  const resultColumns = [
    { key: "date", dataIndex: "reportDate", title: "Tarih", render: (item) => <span className="font-mono text-slate-500">{getTurkishShortDate(item.reportDate)}</span> },
    { key: "case", dataIndex: "caseTitle", title: "Kasa", render: (item) => <span className="font-semibold text-slate-800">{item.caseTitle}</span> },
    { key: "type", dataIndex: "calculationType", title: "Durum", align: "center", render: (item) => getCalculationTypeBadge(item.calculationType) },
    {
      key: "amount",
      dataIndex: "amount",
      title: "Tutar/Fark",
      align: "end",
      render: (item) => {
        const isShortage = item.calculationType === 2;
        const isExcess = item.calculationType === 3;
        return (
          <span className={`font-bold ${isShortage ? "text-red-600" : isExcess ? "text-emerald-600" : "text-slate-700"}`}>
            {isShortage ? "-" : isExcess ? "+" : ""}
            {fmt(item.amount)} ₺
          </span>
        );
      },
    },
  ];

  return (
    <Drawer
      isOpen
      onClose={onClose}
      position="right"
      size={95}
      variant="blur"
      className="!w-[680px] !max-w-[95vw]"
      title={
        <span className="flex flex-col">
          <span className="flex items-center gap-2 text-sm font-bold">
            👥 Kasiyer Bazlı Hesaplama & Aktarım
            <Badge size="xs" radius="full" variant="soft" color="primary">
              {results.length} Kasiyer
            </Badge>
          </span>
          <span className="text-[11px] font-normal text-slate-500">
            Kasiyer bazlı açık/fazla raporlarını iki tarih aralığı ve limit kriterine göre listeleyebilirsiniz.
          </span>
        </span>
      }
      footer={
        <Button variant="outline" color="default" className="!w-full" onClick={onClose}>
          Kapat
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <FieldLabel>Başlangıç Tarihi</FieldLabel>
              <Input size="sm" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} disabled={loading} />
            </div>
            <div className="flex flex-col gap-1">
              <FieldLabel>Bitiş Tarihi</FieldLabel>
              <Input size="sm" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} disabled={loading} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <FieldLabel>Filtre Tipi (Durum)</FieldLabel>
              <Select
                size="sm"
                options={[
                  { value: 0, label: "Hepsi (Tümü)" },
                  { value: 1, label: "Kasa Eşit Olanlar" },
                  { value: 2, label: "Kasa Açığı (Eksik) Olanlar" },
                  { value: 3, label: "Kasa Fazlası Olanlar" },
                ]}
                value={filterType}
                onChange={(e) => setFilterType(Number(e.target.value))}
                disabled={loading}
              />
            </div>
            <div className="flex flex-col gap-1">
              <FieldLabel>Tutar Limiti (Belirtilen Değerden Yüksek)</FieldLabel>
              <Input
                size="sm"
                type="number"
                min="0"
                step="1"
                placeholder="0"
                value={amountCriteria}
                onChange={(e) => setAmountCriteria(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
          <Button className="!w-full" onClick={handleQuery} loading={loading}>
            <span className="flex items-center gap-1.5">
              <Search size={14} /> Sorgula & Listele
            </span>
          </Button>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between px-1">
            <span className="text-[12px] font-bold text-slate-700">📋 Sorgu Sonuçları</span>
            <div className="flex gap-2">
              <Button size="xs" color="success" onClick={handleExportExcel} disabled={results.length === 0 || loading}>
                📊 Excel Aktar
              </Button>
              <Button size="xs" color="danger" onClick={handlePrintPDF} disabled={results.length === 0 || loading}>
                🖨️ PDF Yazdır
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Spinner size={28} label="Veriler hesaplanıyor..." labelPosition="bottom" />
            </div>
          ) : results.length === 0 ? (
            <Empty
              title="Sonuç Bulunamadı"
              description="Seçilen aralıkta ve limit kriterinde herhangi bir kasiyer kaydı bulunmamaktadır."
            />
          ) : (
            results.map((c, cIdx) => (
              <div key={cIdx} className="flex flex-col gap-2.5 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <div className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 p-2">
                  <div className="flex items-center gap-2">
                    <Avatar size="sm" color="blue" name={`${c.cashierName || ""} ${c.cashierSurname || ""}`} />
                    <span className="text-[12.5px] font-bold text-slate-800">
                      {c.cashierName} {c.cashierSurname}
                    </span>
                  </div>
                  <Badge size="xs" radius="full" variant="soft" color="primary">
                    {c.items?.length || 0} İşlem
                  </Badge>
                </div>
                <Table
                  size="xs"
                  uniqueKey="_key"
                  columns={resultColumns}
                  dataSource={(c.items || []).map((item, index) => ({ ...item, _key: String(index) }))}
                />
              </div>
            ))
          )}
        </div>
      </div>
    </Drawer>
  );
};

/* ============================================================
   CloseDayDrawer — Sequential batch closing of reports
============================================================ */
const CloseDayDrawer = ({ date, reports, defs, onClose, onFinish }) => {
  const [drawerKey, setDrawerKey] = useState(0);
  const dispatch = useDispatch();

  // Track status of each report: "pending" | "sending" | "success" | "error"
  // and error messages if any.
  // If r.status === "closed", we show it as "success" by default!
  const [statuses, setStatuses] = useState(
    reports.map((r) => ({
      id: r.id,
      status: r.status === "closed" ? "success" : "pending",
      error: "",
    }))
  );

  const [isProcessing, setIsProcessing] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);

  // Sync statuses if reports change externally
  useEffect(() => {
    setStatuses(
      reports.map((r) => ({
        id: r.id,
        status: r.status === "closed" ? "success" : "pending",
        error: "",
      }))
    );
  }, [reports]);

  const updateReportStatus = (id, status, error = "") => {
    setStatuses((prev) =>
      prev.map((s) => (s.id === id ? { ...s, status, error } : s))
    );
  };

  const startClosureProcess = async (forceAll = false) => {
    if (isProcessing) return;
    setIsProcessing(true);
    setProgressPercent(0);

    // If forceAll, reset all statuses to pending
    let targetReports = [];
    if (forceAll) {
      setStatuses(reports.map((r) => ({ id: r.id, status: "pending", error: "" })));
      targetReports = reports;
    } else {
      // Process only pending or error status
      targetReports = reports.filter((r) => {
        const s = statuses.find((x) => x.id === r.id);
        return s?.status !== "success";
      });
    }

    if (targetReports.length === 0) {
      setIsProcessing(false);
      setProgressPercent(100);
      return;
    }

    dispatch(reopenReports(targetReports.map((r) => r.id)));

    const successIds = [];

    for (let i = 0; i < targetReports.length; i++) {
      const r = targetReports[i];
      updateReportStatus(r.id, "sending");

      const payload = {
        caseId: r.kasaId,
        cashierId: r.kasiyerId,
        no: r.raporNo || "",
        reportDate: r.date,
        cashAmount: Number(r.nakit || 0),
        totalAmount: Number(r.toplamTutar || 0),
        pointAmount: Number(r.hediyeKart || 0),
        numberOfCustomer: String(r.musteriSayisi || "0"),
        entryDebtItems: (r.borclar || []).map((b) => ({
          id: "00000000-0000-0000-0000-000000000000",
          amount: Number(b.tutar || 0),
          definitionDebtId: b.musteriId,
        })),
        entryBankItems: (r.visa || []).map((v) => ({
          id: "00000000-0000-0000-0000-000000000000",
          amount: Number(v.tutar || 0),
          definitionBankId: v.bankaId,
        })),
        entryExpenseItems: (r.giderler || []).map((g) => ({
          id: "00000000-0000-0000-0000-000000000000",
          amount: Number(g.tutar || 0),
          definitionExpenseId: g.kategoriId,
        })),
      };

      try {
        const response = await zreportapi.post("/ZReports", payload);
        if (response.status === 200 || response.status === 201) {
          updateReportStatus(r.id, "success");
          successIds.push(r.id);
        } else {
          updateReportStatus(r.id, "error", `HTTP ${response.status}`);
        }
      } catch (err) {
        const errMsg = err.response?.data?.ExceptionMessage || err.message || "Bilinmeyen Hata";
        updateReportStatus(r.id, "error", errMsg);
      }

      await new Promise((res) => setTimeout(res, 600));
      setProgressPercent(Math.round(((i + 1) / targetReports.length) * 100));
    }

    setIsProcessing(false);
    dispatch(closeDay({ date, successIds }));
    if (onFinish) onFinish();
  };

  const successCount = statuses.filter((s) => s.status === "success").length;
  const errorCount = statuses.filter((s) => s.status === "error").length;
  const pendingCount = statuses.filter((s) => s.status === "pending" || s.status === "error").length;
  const isFinished = progressPercent === 100 && !isProcessing;

  const statusBadge = (status) => {
    const [color, label] = {
      pending: ["default", "Bekliyor"],
      sending: ["primary", "⏳ Gönderiliyor..."],
      success: ["success", "✓ OK"],
      error: ["danger", "✗ Hata"],
    }[status] || ["default", status];
    return (
      <Badge size="sm" variant={status === "success" || status === "error" ? "solid" : "soft"} color={color}>
        {label}
      </Badge>
    );
  };

  // GoldUI Drawer always closes itself on ×/backdrop, so while sending we remount it to keep it open.
  const handleDrawerClose = () => {
    if (isProcessing) {
      goldToast.warning("Gönderim sürerken pencere kapatılamaz.");
      setDrawerKey((k) => k + 1);
      return;
    }
    onClose();
  };

  let footer;
  if (reports.length === 0) {
    footer = (
      <Button color="dark" className="!w-full" onClick={onClose}>
        Kapat
      </Button>
    );
  } else if (isProcessing) {
    footer = (
      <Button className="!w-full" loading disabled>
        Gönderim İşlemi Sürüyor...
      </Button>
    );
  } else if (pendingCount > 0) {
    footer = (
      <div className="flex w-full gap-2">
        <Button color="danger" className="!w-full" onClick={() => startClosureProcess(false)}>
          🚀 Gönderimi Başlat ({pendingCount} Rapor)
        </Button>
        <Button variant="outline" color="default" onClick={onClose}>
          Vazgeç
        </Button>
      </div>
    );
  } else {
    footer = (
      <div className="flex w-full flex-col gap-2">
        <Alert size="xs" variant="filled" color="success" description="✓ Tüm raporlar başarıyla gönderildi." />
        <div className="flex gap-2">
          <Button className="!w-full" onClick={() => startClosureProcess(true)}>
            🔄 Tümünü Yeniden Gönder
          </Button>
          <Button color="dark" onClick={onClose}>
            Kapat
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Drawer
      key={drawerKey}
      isOpen
      onClose={handleDrawerClose}
      position="right"
      size={95}
      variant="blur"
      className="!w-[460px] !max-w-[95vw]"
      title={
        <span className="flex flex-col">
          <span className="flex items-center gap-2 text-sm font-bold">
            🔒 Günü Kapat
            <Badge size="xs" variant="soft" color="danger">
              {date}
            </Badge>
          </span>
          <span className="text-[11px] font-normal text-slate-500">{reports.length} adet Z-Raporu listeleniyor.</span>
        </span>
      }
      footer={
        <div className="flex w-full flex-col gap-2">
          {reports.length > 0 && isFinished && (
            <Alert
              size="sm"
              variant="outline"
              color={errorCount > 0 ? "warning" : "success"}
              title="Kapatma Sonucu"
              description={`Başarılı: ${successCount} · Hatalı: ${errorCount}. Günü kapatma işlemi tamamlandı ve yerel durum güncellendi.`}
            />
          )}
          {footer}
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        {progressPercent > 0 && <Progress value={progressPercent} size="sm" color="primary" showInfo />}

        {reports.length === 0 ? (
          <Empty size="sm" title="Kapatılacak rapor yok" description="Bu tarihte kapatılacak kaydedilmemiş açık Z-Raporu bulunmamaktadır." />
        ) : (
          reports.map((r, idx) => {
            const state = statuses.find((s) => s.id === r.id) || { status: "pending", error: "" };
            const cashier = (defs.kasiyerler || []).find((x) => x.id === r.kasiyerId);
            const cashierName = cashier ? `${cashier.name} ${cashier.surname}` : "Bilinmeyen Kasiyer";
            const cs = (defs.kasalar || []).find((x) => x.id === r.kasaId);
            const caseName = cs ? cs.title || `${cs.no} - ${cs.name}` : "Bilinmeyen Kasa";

            return (
              <div
                key={r.id}
                className={`flex flex-col gap-1 rounded-xl border p-3 transition-colors ${
                  state.status === "success"
                    ? "border-green-200 bg-green-50/40"
                    : state.status === "error"
                    ? "border-red-200 bg-red-50/40"
                    : state.status === "sending"
                    ? "animate-pulse border-blue-300 bg-blue-50/40"
                    : "border-slate-200 bg-white"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[13px] font-bold text-slate-800">Rapor No: {r.raporNo || `Kart #${idx + 1}`}</div>
                    <div className="mt-0.5 text-[11px] text-slate-500">Kasiyer: {cashierName}</div>
                    <div className="text-[11px] text-slate-500">Kasa: {caseName}</div>
                  </div>
                  {statusBadge(state.status)}
                </div>

                {state.error && <Alert size="xs" variant="filled" color="danger" description={state.error} />}

                <div className="mt-2 flex justify-between border-t border-slate-100 pt-2 text-[11px] text-slate-500">
                  <span>
                    Toplam: <b>{fmt(r.toplamTutar)} ₺</b>
                  </span>
                  <span>
                    Nakit: <b>{fmt(r.nakit)} ₺</b>
                  </span>
                  <span>
                    Kart: <b>{fmt(r.hediyeKart)} ₺</b>
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </Drawer>
  );
};

/* ============================================================
   Raporlar (geçmiş) modal — list of past closed days
============================================================ */
const RaporlarModal = ({ onClose, onPickDate }) => {
  const closures = useSelector((s) => s.zRapor.dailyClosure);
  const reports = useSelector((s) => s.zRapor.reports);

  const dates = useMemo(() => {
    const set = new Set();
    reports.forEach((r) => set.add(r.date));
    closures.forEach((c) => set.add(c.date));
    return [...set].sort().reverse();
  }, [reports, closures]);

  return (
    <Modal title="Geçmiş Raporlar" onClose={onClose} width={780}>
      <div className="flex flex-col gap-3">
        {dates.length === 0 && <Empty size="sm" title="Henüz kayıt yok" />}
        {dates.map((d) => {
          const dayReports = reports.filter((r) => r.date === d);
          const closure = closures.find((c) => c.date === d);
          const total = sumBy(dayReports, (r) => r.toplamTutar);
          const tahsil = sumBy(dayReports, (r) => tahsilatToplam(r));
          return (
            <div
              key={d}
              role="button"
              tabIndex={0}
              className="cursor-pointer rounded-xl border border-slate-200 p-3 transition-colors hover:border-amber-300 hover:bg-amber-50/40"
              onClick={() => {
                onPickDate(d);
                onClose();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  onPickDate(d);
                  onClose();
                }
              }}
              data-testid={`raporlar-row-${d}`}
            >
              <div className="mb-1 flex items-center justify-between">
                <div className="text-[14px] font-semibold">
                  {new Date(d + "T00:00:00").toLocaleDateString("tr-TR", {
                    weekday: "long",
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  })}
                </div>
                <Badge size="xs" variant="soft" color={closure ? "success" : "warning"}>
                  {closure ? "KAPATILDI" : "AÇIK"}
                </Badge>
              </div>
              <div className="flex flex-wrap gap-4 text-[12px] text-slate-500">
                <span>📊 {dayReports.length} rapor</span>
                <span>
                  💰 Z-Toplam: <b>{fmt(total)} ₺</b>
                </span>
                <span>
                  💳 Tahsilat: <b>{fmt(tahsil)} ₺</b>
                </span>
                {closure && <span>🔒 {new Date(closure.closedAt).toLocaleString("tr-TR")}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

/* ============================================================
   Rapor Detay İnceleme ve Düzenleme Modal (Closed Report Edit)
============================================================ */
const isGuid = (id) => {
  if (!id) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(id));
};

const EditClosedReportModal = ({ report, onClose, defs }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  // Top level fields
  const [localRaporNo, setLocalRaporNo] = useState(report.raporNo || "");
  const [localToplamTutar, setLocalToplamTutar] = useState(report.toplamTutar || 0);
  const [localNakit, setLocalNakit] = useState(report.nakit || 0);
  const [localHediyeKart, setLocalHediyeKart] = useState(report.hediyeKart || 0);
  const [localMusteriSayisi, setLocalMusteriSayisi] = useState(report.numberOfCustomer || report.musteriSayisi || "0");

  // Child table fields
  const [localVisa, setLocalVisa] = useState(report.visa || []);
  const [localGiderler, setLocalGiderler] = useState(report.giderler || []);
  const [localBorclar, setLocalBorclar] = useState(report.borclar || []);

  // Initial copies for comparison
  const initialVisa = useMemo(() => report.visa || [], [report]);
  const initialGiderler = useMemo(() => report.giderler || [], [report]);
  const initialBorclar = useMemo(() => report.borclar || [], [report]);

  const tempId = () => "temp_" + Math.random().toString(36).substr(2, 9);

  // Totals calculations for difference badge
  const localTahsilatTotal = 
    Number(localNakit || 0) +
    Number(localHediyeKart || 0) +
    localVisa.reduce((s, v) => s + Number(v.tutar || 0), 0) +
    localBorclar.reduce((s, b) => s + Number(b.tutar || 0), 0) +
    localGiderler.reduce((s, g) => s + Number(g.tutar || 0), 0);

  const localFark = localTahsilatTotal - Number(localToplamTutar || 0);
  const farkAbs = Math.abs(localFark).toFixed(2);
  
  let badge = null;
  if (Number(localToplamTutar) > 0 || localTahsilatTotal > 0) {
    if (Math.abs(localFark) < 0.01)
      badge = { text: "TAM", color: "default" };
    else if (localFark > 0)
      badge = { text: `FAZLA +${farkAbs}`, color: "success" };
    else
      badge = { text: `EKSİK −${farkAbs}`, color: "danger" };
  }

  const cashier = (defs.kasiyerler || []).find((x) => x.id === report.kasiyerId);
  const cashierName = cashier ? `${cashier.name} ${cashier.surname}` : "—";
  const cs = (defs.kasalar || []).find((x) => x.id === report.kasaId);
  const caseName = cs ? (cs.title || `${cs.no} - ${cs.name}`) : "—";

  const handleSave = async () => {
    setLoading(true);
    try {
      // 1. Check parent changed
      const parentChanged = 
        report.raporNo !== localRaporNo ||
        Number(report.toplamTutar) !== Number(localToplamTutar) ||
        Number(report.nakit) !== Number(localNakit) ||
        Number(report.hediyeKart) !== Number(localHediyeKart) ||
        String(report.numberOfCustomer || report.musteriSayisi || "0") !== String(localMusteriSayisi);

      if (parentChanged) {
        try {
          await zreportapi.put(`ZReports/${report.id}`, {
            caseId: report.kasaId,
            cashierId: report.kasiyerId,
            no: localRaporNo,
            reportDate: report.date,
            cashAmount: Number(localNakit),
            totalAmount: Number(localToplamTutar),
            pointAmount: Number(localHediyeKart),
            numberOfCustomer: String(localMusteriSayisi)
          });
        } catch (parentErr) {
          console.warn("Parent ZReport PUT is not implemented or failed, skipping:", parentErr);
        }
      }

      // 2. Process Visa (EntryBanks)
      const savedVisa = [...localVisa];
      for (let i = 0; i < savedVisa.length; i++) {
        const item = savedVisa[i];
        if (!isGuid(item.id)) {
          const res = await zreportapi.post("EntryBanks", {
            amount: Number(item.tutar),
            zReportId: report.id,
            definitionBankId: item.bankaId
          });
          savedVisa[i] = { ...item, id: res.data?.id || item.id };
        } else {
          const init = initialVisa.find((x) => x.id === item.id);
          if (init && (Number(init.tutar) !== Number(item.tutar) || init.bankaId !== item.bankaId)) {
            await zreportapi.put(`EntryBanks/${item.id}`, {
              amount: Number(item.tutar),
              zReportId: report.id,
              definitionBankId: item.bankaId
            });
          }
        }
      }
      for (const init of initialVisa) {
        if (!localVisa.some((x) => x.id === init.id)) {
          if (isGuid(init.id)) {
            await zreportapi.delete(`EntryBanks/${init.id}`);
          }
        }
      }

      // 3. Process Debts (EntryDebts)
      const savedBorclar = [...localBorclar];
      for (let i = 0; i < savedBorclar.length; i++) {
        const item = savedBorclar[i];
        if (!isGuid(item.id)) {
          const res = await zreportapi.post("EntryDebts", {
            amount: Number(item.tutar),
            zReportId: report.id,
            definitionDebtId: item.musteriId
          });
          savedBorclar[i] = { ...item, id: res.data?.id || item.id };
        } else {
          const init = initialBorclar.find((x) => x.id === item.id);
          if (init && (Number(init.tutar) !== Number(item.tutar) || init.musteriId !== item.musteriId)) {
            await zreportapi.put(`EntryDebts/${item.id}`, {
              amount: Number(item.tutar),
              zReportId: report.id,
              definitionDebtId: item.musteriId
            });
          }
        }
      }
      for (const init of initialBorclar) {
        if (!localBorclar.some((x) => x.id === init.id)) {
          if (isGuid(init.id)) {
            await zreportapi.delete(`EntryDebts/${init.id}`);
          }
        }
      }

      // 4. Process Expenses (EntryExpenses)
      const savedGiderler = [...localGiderler];
      for (let i = 0; i < savedGiderler.length; i++) {
        const item = savedGiderler[i];
        if (!isGuid(item.id)) {
          const res = await zreportapi.post("EntryExpenses", {
            amount: Number(item.tutar),
            zReportId: report.id,
            definitionExpenseId: item.kategoriId
          });
          savedGiderler[i] = { ...item, id: res.data?.id || item.id };
        } else {
          const init = initialGiderler.find((x) => x.id === item.id);
          if (init && (Number(init.tutar) !== Number(item.tutar) || init.kategoriId !== item.kategoriId || init.aciklama !== item.aciklama)) {
            await zreportapi.put(`EntryExpenses/${item.id}`, {
              amount: Number(item.tutar),
              zReportId: report.id,
              definitionExpenseId: item.kategoriId
            });
          }
        }
      }
      for (const init of initialGiderler) {
        if (!localGiderler.some((x) => x.id === init.id)) {
          if (isGuid(init.id)) {
            await zreportapi.delete(`EntryExpenses/${init.id}`);
          }
        }
      }

      // 5. Update Redux store
      dispatch(updateReport({
        id: report.id,
        patch: {
          raporNo: localRaporNo,
          toplamTutar: Number(localToplamTutar),
          nakit: Number(localNakit),
          hediyeKart: Number(localHediyeKart),
          musteriSayisi: localMusteriSayisi,
          numberOfCustomer: String(localMusteriSayisi),
          visa: savedVisa,
          borclar: savedBorclar,
          giderler: savedGiderler
        }
      }));

      goldAlert.fire({
        icon: "success",
        title: "Değişiklikler Kaydedildi",
        text: "Z-Raporu ve bağlı verileri başarıyla güncellendi.",
        timer: 1500,
        showConfirmButton: false,
        toast: true,
        position: "top-end"
      });

      onClose();
    } catch (err) {
      console.error("Save error:", err);
      const errMsg = err.response?.data?.ExceptionMessage || err.message || "Bilinmeyen bir hata oluştu.";
      goldAlert.fire({
        icon: "error",
        title: "Kayıt Başarısız",
        text: errMsg
      });
    } finally {
      setLoading(false);
    }
  };

  const patchRow = (setter, list, id, patch) => setter(list.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  const childSection = (title, rows, setter, placeholder, list, idKey, blankRow) => (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <Section
        title={title}
        rows={rows}
        onAdd={() => setter([...rows, { id: tempId(), ...blankRow }])}
        onDelete={(id) => setter(rows.filter((x) => x.id !== id))}
        emptyText="Kayıt yok"
        disabled={false}
        renderRow={(r) => (
          <>
            <div className="min-w-0 flex-1">
              <Select
                size="sm"
                options={toOptions(list, (x) => x.name, placeholder)}
                value={r[idKey]}
                onChange={(e) => patchRow(setter, rows, r.id, { [idKey]: e.target.value })}
              />
            </div>
            <AmountInput width={100} value={r.tutar} onChange={(v) => patchRow(setter, rows, r.id, { tutar: v })} />
          </>
        )}
      />
    </div>
  );

  const infoItem = (label, value) => (
    <div>
      <div className="text-[10px] font-bold uppercase text-slate-500">{label}</div>
      <div className="text-[13px] font-semibold text-slate-800">{value}</div>
    </div>
  );

  return (
    <Modal title={`Rapor Detay İnceleme & Güncelleme - ${report.raporNo || "Rapor"}`} onClose={onClose} width={640}>
      <div className="flex flex-col gap-4 text-[13px]">
        <div className="grid grid-cols-2 gap-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
          {infoItem("Tarih", report.date)}
          <div>
            <div className="text-[10px] font-bold uppercase text-slate-500">Durum</div>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
              <Badge size="sm" variant="soft" color="success">
                ● Kapatıldı (Gönderildi)
              </Badge>
              {badge && (
                <Badge size="sm" variant="soft" color={badge.color}>
                  {badge.text}
                </Badge>
              )}
            </div>
          </div>
          {infoItem("Kasiyer", cashierName)}
          {infoItem("Kasa", caseName)}
        </div>

        <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3">
          <div className="text-[12px] font-bold text-slate-700">Rapor Genel Bilgileri</div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            <div className="flex flex-col gap-1">
              <FieldLabel>Rapor No</FieldLabel>
              <Input size="sm" value={localRaporNo} onChange={(e) => setLocalRaporNo(e.target.value)} />
            </div>
            <div className="flex flex-col gap-1">
              <FieldLabel>Z-Rapor Tutar</FieldLabel>
              <AmountInput value={localToplamTutar} onChange={setLocalToplamTutar} />
            </div>
            <div className="flex flex-col gap-1">
              <FieldLabel>Nakit</FieldLabel>
              <AmountInput value={localNakit} onChange={setLocalNakit} />
            </div>
            <div className="flex flex-col gap-1">
              <FieldLabel>Kart/Hediye</FieldLabel>
              <AmountInput value={localHediyeKart} onChange={setLocalHediyeKart} />
            </div>
            <div className="flex flex-col gap-1">
              <FieldLabel>Müşteri Sayısı</FieldLabel>
              <Input size="sm" className="!text-center" value={localMusteriSayisi} onChange={(e) => setLocalMusteriSayisi(e.target.value)} />
            </div>
          </div>
        </div>

        {childSection("💳 KREDİ KARTI / BANKA TAHSİLATLARI", localVisa, setLocalVisa, "— Banka —", defs.bankalar, "bankaId", { bankaId: "", tutar: 0 })}
        {childSection("💸 KASA GİDERLERİ", localGiderler, setLocalGiderler, "— Kategori —", defs.giderKategoriler, "kategoriId", { kategoriId: "", tutar: 0 })}
        {childSection("👤 VERİLEN BORÇLAR", localBorclar, setLocalBorclar, "— Müşteri —", defs.borcMusteriler, "musteriId", { musteriId: "", tutar: 0 })}

        <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
          <Button variant="outline" color="default" onClick={onClose} disabled={loading}>
            Vazgeç
          </Button>
          <Button onClick={handleSave} loading={loading}>
            ✓ Değişiklikleri Kaydet
          </Button>
        </div>
      </div>
    </Modal>
  );
};

/* ============================================================
   Main page
============================================================ */
const ZRaporPage = () => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const reports = useSelector((s) => s.zRapor.reports);
  const closures = useSelector((s) => s.zRapor.dailyClosure);

  const [date, setDate] = useState(today());
  const [search, setSearch] = useState("");
  const [showTanimlar, setShowTanimlar] = useState(false);
  const [showOdemeler, setShowOdemeler] = useState(false);
  const [showRaporlar, setShowRaporlar] = useState(false);
  const [showCloseDayDrawer, setShowCloseDayDrawer] = useState(false);
  const [inspectReport, setInspectReport] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showCashierCalculation, setShowCashierCalculation] = useState(false);

  const defs = useSelector((s) => s.zRaporDefs);

  useEffect(() => {
    dispatch(fetchAllZReportDefs(user?.branchId));
  }, [dispatch, user?.branchId]);

  const loadZReportsFromBackend = async (selectedDate) => {
    if (!user?.branchId) return;
    try {
      const response = await zreportapi.get(`ZReports/branch/${user.branchId}`, {
        params: {
          page: 1,
          size: 100,
          reportDate: selectedDate
        }
      });
      const items = response.data?.items || [];
      const mapped = items.map((item) => ({
        id: item.id,
        date: item.reportDate,
        kasiyerId: item.cashierId,
        kasaId: item.caseId,
        raporNo: item.no,
        toplamTutar: Number(item.totalAmount || 0),
        musteriSayisi: Number(item.numberOfCustomer || 0),
        nakit: Number(item.cashAmount || 0),
        hediyeKart: Number(item.pointAmount || 0),
        visa: (item.entryBankItems || []).map((b) => ({
          id: b.id,
          bankaId: b.definitionBankId,
          tutar: Number(b.amount || 0)
        })),
        giderler: (item.entryExpenseItems || []).map((e) => ({
          id: e.id,
          kategoriId: e.definitionExpenseId,
          tutar: Number(e.amount || 0)
        })),
        borclar: (item.entryDebtItems || []).map((d) => ({
          id: d.id,
          musteriId: d.definitionDebtId,
          tutar: Number(d.amount || 0)
        })),
        status: "closed",
        createdAt: item.createdAt,
        closedAt: item.updatedAt || item.createdAt
      }));

      dispatch(setReportsForDate({ date: selectedDate, backendReports: mapped }));
    } catch (err) {
      console.error("Failed to load backend Z-reports:", err);
    }
  };

  useEffect(() => {
    if (user?.branchId && date) {
      loadZReportsFromBackend(date);
    }
  }, [date, user?.branchId]);

  const filtered = useMemo(() => {
    let list = reports.filter((r) => r.date === date);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) => {
        const k = defs.kasiyerler.find((x) => x.id === r.kasiyerId);
        const kname = k ? `${k.name} ${k.surname}`.toLowerCase() : "";
        return (
          (r.raporNo || "").toLowerCase().includes(q) ||
          kname.includes(q)
        );
      });
    }
    return list;
  }, [reports, date, search, defs.kasiyerler]);

  const activeReports = useMemo(() => filtered.filter((r) => r.status !== "closed"), [filtered]);
  const closedReports = useMemo(() => filtered.filter((r) => r.status === "closed"), [filtered]);

  const dayClosed = closures.find((c) => c.date === date);
  const openCount = filtered.filter((r) => r.status !== "closed").length;

  const handleNew = () => {
    const defaultVisa = (defs.bankalar || [])
      .filter((b) => b.isSelected === true)
      .map((b) => ({
        id: "temp_" + Math.random().toString(36).substr(2, 9),
        bankaId: b.id,
        tutar: 0,
      }));

    dispatch(
      addReport({
        date,
        kasiyerId: "",
        kasaId: "",
        raporNo: "",
        toplamTutar: 0,
        musteriSayisi: 0,
        nakit: 0,
        hediyeKart: 0,
        visa: defaultVisa,
      })
    );
  };

  const handleCloseDay = () => {
    if (user?.branchId && date) {
      loadZReportsFromBackend(date);
    }
    setShowCloseDayDrawer(true);
  };

  const isGuid = (id) => typeof id === "string" && id.length === 36;

  const handleDeleteClosedReport = async (report) => {
    goldAlert.fire({
      title: "Raporu Silmek İstiyor musunuz?",
      text: "Bu işlem geri alınamaz! Rapor veritabanından kalıcı olarak silinecektir.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "danger",
      cancelButtonColor: "default",
      confirmButtonText: "Evet, Sil",
      cancelButtonText: "Vazgeç",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          if (isGuid(report.id)) {
            await zreportapi.delete(`ZReports/${report.id}`);
          }
          dispatch(deleteReport(report.id));
          goldAlert.fire("Silindi!", "Z-Raporu başarıyla silindi.", "success");
          if (user?.branchId && date) {
            loadZReportsFromBackend(date);
          }
        } catch (err) {
          console.error("Failed to delete closed Z-report:", err);
          goldAlert.fire("Hata!", "Rapor silinirken bir hata oluştu.", "error");
        }
      }
    });
  };


  const closedColumns = [
    {
      key: "raporNo",
      dataIndex: "raporNo",
      title: "Rapor No",
      render: (r) => <span className="font-bold text-slate-800">{r.raporNo || `ID: ${String(r.id).substring(2, 8)}`}</span>,
    },
    {
      key: "cashier",
      dataIndex: "kasiyerId",
      title: "Kasiyer",
      render: (r) => {
        const cashier = (defs.kasiyerler || []).find((x) => x.id === r.kasiyerId);
        return cashier ? `${cashier.name} ${cashier.surname}` : "—";
      },
    },
    {
      key: "case",
      dataIndex: "kasaId",
      title: "Kasa",
      render: (r) => {
        const cs = (defs.kasalar || []).find((x) => x.id === r.kasaId);
        return cs ? cs.title || `${cs.no} - ${cs.name}` : "—";
      },
    },
    { key: "total", dataIndex: "toplamTutar", title: "Toplam Tutar", align: "end", render: (r) => <b className="text-slate-900">{fmt(r.toplamTutar)} ₺</b> },
    { key: "cash", dataIndex: "nakit", title: "Nakit", align: "end", render: (r) => `${fmt(r.nakit)} ₺` },
    { key: "card", dataIndex: "hediyeKart", title: "Kart/Hediye", align: "end", render: (r) => `${fmt(r.hediyeKart)} ₺` },
    {
      key: "closedAt",
      dataIndex: "closedAt",
      title: "Kapatılma Zamanı",
      align: "center",
      render: (r) => <span className="text-[11px] text-slate-500">{r.closedAt ? new Date(r.closedAt).toLocaleString("tr-TR") : "—"}</span>,
    },
    {
      key: "actions",
      dataIndex: "id",
      title: "İşlemler",
      align: "center",
      render: (r) => (
        <div className="flex items-center justify-center gap-1.5">
          <Button size="xs" variant="filled" color="warning" onClick={() => setInspectReport(r)} title="Detayları düzenlemek için bu kapalı raporu günceller">
            Güncelle
          </Button>
          <Button size="xs" variant="filled" color="primary" onClick={() => setInspectReport(r)}>
            İncele
          </Button>
          <Button size="xs" variant="filled" color="danger" onClick={() => handleDeleteClosedReport(r)} title="Bu kapalı raporu veritabanından kalıcı olarak siler">
            Sil
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AppShell pageId="zrapor">
      <ClassicWindow title="Z-Rapor İşlemleri" width="100%">
        <Toolbar>
          <div className="w-40">
            <Input size="sm" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="zrapor-toolbar-date" />
          </div>
          <div className="w-64">
            <Input
              size="sm"
              icon={<Search size={14} />}
              placeholder="Rapor no veya kasiyer adı ile ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="zrapor-search"
            />
          </div>
          <ToolbarButton icon="⚙" label="Tanımlar" onClick={() => setShowTanimlar(true)} testId="btn-tanimlar" />
          <ToolbarButton icon="₺" label="Ödemeler" onClick={() => setShowOdemeler(true)} testId="btn-odemeler" />
          <ToolbarButton icon="📤" label="Hesapları Aktar" onClick={() => setShowExportModal(true)} testId="btn-export" />
          <ToolbarButton icon="👥" label="Kasiyer Bazlı Aktarım" onClick={() => setShowCashierCalculation(true)} testId="btn-cashier-calc" />
          <ToolbarSeparator />
          <ToolbarButton icon="🔒" label="Günü Kapat" active color="danger" onClick={handleCloseDay} testId="btn-close-day" />
          <ToolbarButton icon="➕" label="Yeni Rapor" active color="primary" onClick={handleNew} testId="btn-new-rapor" />
        </Toolbar>

        <div className="p-3 sm:p-4">
          <div className="mb-3 mt-1 flex flex-wrap items-center justify-between gap-2">
            <h2 className="flex items-center gap-2 text-[13px] font-bold uppercase text-slate-900">
              📂 Aktif (Açık) Z-Raporları
              <Badge size="xs" radius="full" variant="soft" color="primary">
                {activeReports.length} Rapor
              </Badge>
            </h2>
            <div className="flex items-center gap-2 text-[12px] text-slate-500">
              <span>
                <b className="text-slate-800">{filtered.length}</b> toplam rapor (Açık: {activeReports.length}, Kapalı: {closedReports.length})
              </span>
              {dayClosed && (
                <Badge size="xs" variant="soft" color="success">
                  ✓ Gün Kapatıldı
                </Badge>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-start gap-3">
            {activeReports.length === 0 && (
              <div className="w-full rounded-xl border border-dashed border-slate-200 bg-slate-50/60">
                <Empty
                  title="Bu tarihte aktif (açık) rapor yok"
                  description="Araç çubuğundaki Yeni Rapor ile yeni bir Z-Rapor kartı ekleyebilirsiniz."
                >
                  <Button size="sm" onClick={handleNew}>
                    <span className="flex items-center gap-1.5">
                      <Plus size={14} /> Yeni Rapor
                    </span>
                  </Button>
                </Empty>
              </div>
            )}
            {activeReports.map((r) => (
              <ZRaporCard key={r.id} report={r} />
            ))}
          </div>

          {closedReports.length > 0 && (
            <div className="mt-8 border-t border-slate-200 pt-5">
              <h2 className="mb-3 flex items-center gap-2 text-[13px] font-bold uppercase text-emerald-700">
                🔒 Kapatılmış / Gönderilmiş Raporlar (Geçmiş)
                <Badge size="xs" radius="full" variant="soft" color="success">
                  {closedReports.length} Rapor
                </Badge>
              </h2>
              <div className="-mx-3 sm:-mx-4">
                <Table size="sm" uniqueKey="id" columns={closedColumns} dataSource={closedReports} scroll={{ x: 980 }} />
              </div>
            </div>
          )}
        </div>
      </ClassicWindow>

      {/* Modals */}
      {showTanimlar && <TanimlarModal onClose={() => setShowTanimlar(false)} />}
      {showOdemeler && (
        <OdemelerModal date={date} onClose={() => setShowOdemeler(false)} />
      )}
      {showRaporlar && (
        <RaporlarModal
          onClose={() => setShowRaporlar(false)}
          onPickDate={(d) => setDate(d)}
        />
      )}
      {showExportModal && (
        <ExportModal
          onClose={() => setShowExportModal(false)}
          initialDate={date}
        />
      )}
      {showCashierCalculation && (
        <CashierCalculationDrawer onClose={() => setShowCashierCalculation(false)} />
      )}
      {showCloseDayDrawer && (
        <CloseDayDrawer
          date={date}
          reports={filtered.filter((r) => r.status !== "closed")}
          defs={defs}
          onClose={() => setShowCloseDayDrawer(false)}
          onFinish={() => {
            loadZReportsFromBackend(date);
          }}
        />
      )}
      {inspectReport && (
        <EditClosedReportModal
          report={inspectReport}
          defs={defs}
          onClose={() => setInspectReport(null)}
        />
      )}
    </AppShell>
  );
};

export default ZRaporPage;
